/**
 * Lexikon: Typen, Daten und Helfer.
 *
 * Alle Einträge sind eigenständig und original für ncrechner geschrieben.
 * Die Referenzseite diente nur als Themen-Inspiration – kein übernommener
 * Text, keine fremden Marken. Struktur bewusst API-nah für einen späteren
 * CMS-Wechsel.
 */

export type LexikonFaq = { question: string; answer: string };

/** Optionaler, weiterführender Ratgeber-Link zu einem Begriff. */
export type LexikonArticleLink = { label: string; href: string };

/** Strukturierter Inhaltsabschnitt eines ausführlichen Lexikon-Eintrags. */
export type LexikonSection = {
  /** Anker-ID fürs Inhaltsverzeichnis. */
  id: string;
  heading: string;
  paragraphs: string[];
  /** Optionale Aufzählung am Ende des Abschnitts. */
  list?: string[];
};

export type LexikonTerm = {
  /** URL-Slug, z. B. "numerus-clausus". */
  slug: string;
  /** Anzeigename, z. B. "Numerus Clausus (NC)". */
  term: string;
  /** Thematische Gruppe für Related-Fallback und Filter. */
  category: "Quoten" | "Tests" | "Bewerbung" | "Studium" | "Grundbegriffe";
  /** Einzeiler für Karten, Meta-Description und Vorschau. */
  short: string;
  /** Antwort-zuerst-Definition (40–60 Wörter). */
  definition: string;
  /** Ausführliche Erklärung in kurzen Absätzen (Fallback ohne `sections`). */
  body: string[];
  /** Optionales Titelbild für ausführliche Einträge. */
  heroImage?: string;
  heroImageAlt?: string;
  /** Strukturierte Abschnitte mit Überschriften (aktivieren TOC-Sidebar). */
  sections?: LexikonSection[];
  /** Slugs verwandter Lexikon-Begriffe. */
  related?: string[];
  /** Weiterführender Ratgeber-Artikel. */
  article?: LexikonArticleLink;
  /**
   * Zusätzlicher apoBank-Anzeigenblock vor dem 2. Abschnitt. Läuft parallel
   * zu den rotierenden Partner-CTAs, ersetzt sie nicht.
   */
  apoBankCta?: import("@/components/shared/ApoBankCta").ApoBankCtaVariant;
  faqs?: LexikonFaq[];
  seoTitle?: string;
  metaDescription?: string;
};

/** Ordnet Umlaut-Anfangsbuchstaben ihrem Grundbuchstaben zu. */
export function firstLetter(term: string): string {
  const c = term.trim().charAt(0).toUpperCase();
  if (c === "Ä") return "A";
  if (c === "Ö") return "O";
  if (c === "Ü") return "U";
  return c;
}

const collator = new Intl.Collator("de", { sensitivity: "base" });

/** Alle Begriffe, alphabetisch (deutsche Sortierung). */
export const lexikonTerms: LexikonTerm[] = [
  {
    slug: "abiturbestenquote",
    term: "Abiturbestenquote (ABQ)",
    category: "Quoten",
    short:
      "Vergibt rund 30 % der Medizin-Studienplätze allein nach der Abiturnote – verglichen in 16 Landesquoten.",
    definition:
      "Die Abiturbestenquote (ABQ) vergibt rund 30 Prozent der Medizin-Studienplätze allein nach der Abiturnote. Verglichen wird innerhalb von 16 Landesquoten, also nur mit Bewerbern des eigenen Bundeslandes. In der Praxis reicht es hier meist nur mit einem Spitzenabitur im Bereich von etwa 1,0 bis 1,2.",
    seoTitle: "Abiturbestenquote (ABQ) einfach erklärt | ncrechner",
    metaDescription:
      "Die Abiturbestenquote vergibt rund 30 % der Medizin-Studienplätze allein nach der Note – verglichen in 16 Landesquoten. Was zählt, was nicht und wer Chancen.",
    heroImage: "/images/blog/z-abq.jpg",
    heroImageAlt: "Abiturzeugnis als Grundlage der Abiturbestenquote",
    body: [],
    sections: [
      {
        id: "grundlage",
        heading: "So funktioniert die ABQ",
        paragraphs: [
          "Die ABQ ist die erste der drei großen Quoten und richtet sich an die Bewerber mit den stärksten Abiturnoten. Sie steht vor dem Auswahlverfahren der Hochschulen (AdH) und der Zusätzlichen Eignungsquote (ZEQ), über die die übrigen Plätze vergeben werden.",
        ],
      },
      {
        id: "landesquoten",
        heading: "16 Landesquoten statt bundesweiter Liste",
        paragraphs: [
          "Statt einer bundesweiten Rangliste gibt es 16 Landesquoten – eine je Bundesland. Du konkurrierst also zunächst nur mit Bewerbern aus deinem eigenen Land. Der Grund: Abiturnoten sind zwischen den Bundesländern nicht direkt vergleichbar. Wie viele Plätze auf ein Land entfallen, richtet sich vor allem nach der Zahl seiner Abiturienten.",
        ],
      },
      {
        id: "note",
        heading: "Welche Note du brauchst",
        paragraphs: [
          "Welche Note reicht, schwankt je Bundesland und Jahrgang, liegt aber meist im engen Fenster von rund 1,0 bis 1,2. Entscheidend ist: In der ABQ lässt sich eine schwächere Note durch nichts ausgleichen. Es zählt einzig die Abiturdurchschnittsnote.",
        ],
      },
      {
        id: "was-zaehlt",
        heading: "Was zählt – und was nicht",
        paragraphs: [
          "Nicht gewertet werden hier Studierfähigkeitstests wie TMS oder HAM-Nat, Freiwilligendienste, abgeschlossene Ausbildungen, frühere Wartezeit, Motivationsschreiben oder Auswahlgespräche. All diese Bausteine wirken erst im AdH und in der ZEQ – nicht in der Abiturbestenquote.",
        ],
      },
      {
        id: "unbesetzt",
        heading: "Nicht besetzte Plätze",
        paragraphs: [
          "Bleiben in einer Landesquote Plätze unbesetzt, entsteht keine eigene ABQ-Warteliste. Die freien Plätze fließen stattdessen in das Auswahlverfahren der Hochschulen und werden dort weiter vergeben.",
        ],
      },
      {
        id: "bewerbung",
        heading: "Bewerbung und für wen die ABQ passt",
        paragraphs: [
          "Eine separate Bewerbung für die ABQ gibt es nicht. Wer sich über hochschulstart.de bewirbt, wird automatisch auch in der Abiturbestenquote berücksichtigt. Reicht die Note dort nicht, läuft die Bewerbung ohne weiteres Zutun in AdH und ZEQ weiter.",
          "Realistisch ist die ABQ deshalb vor allem für Abiturnoten nahe 1,0. Liegt dein Schnitt darüber, sind AdH und ZEQ die stärkeren Hebel – dort zählen Test, Dienst und Ausbildung. Mit der ncrechner App siehst du, ob deine Note in der Landesquote deines Bundeslandes reicht und wo dir die anderen Quoten bessere Chancen bieten.",
        ],
      },
    ],
    related: [
      "auswahlverfahren-der-hochschulen",
      "zusaetzliche-eignungsquote",
      "numerus-clausus",
      "hochschulstart",
    ],
    article: {
      label: "Abiturbestenquote (ABQ) im Detail",
      href: "/ratgeber/zulassung/abiturbestenquote-abq-erklaert/",
    },
    faqs: [
      {
        question: "Wie viele Studienplätze gehen über die ABQ?",
        answer:
          "Rund 30 Prozent der Plätze je Studiengang. Die Auswahl erfolgt allein nach der Abiturnote. Die übrigen etwa 60 Prozent (AdH) und 10 Prozent (ZEQ) werden über andere Kriterien vergeben.",
      },
      {
        question: "Welche Abiturnote brauche ich für die ABQ?",
        answer:
          "Meist einen Schnitt im Bereich von etwa 1,0 bis 1,2. Der genaue Wert schwankt je Bundesland und Jahrgang, weil in jeder der 16 Landesquoten getrennt ausgewählt wird.",
      },
      {
        question: "Warum wird die Note innerhalb des Bundeslandes verglichen?",
        answer:
          "Weil Abiturnoten zwischen den Bundesländern nicht direkt vergleichbar sind. Deshalb bildet die ABQ 16 Landesquoten, in denen du nur gegen Bewerber aus deinem eigenen Land antrittst.",
      },
      {
        question: "Zählen TMS, Dienst oder Wartezeit in der ABQ?",
        answer:
          "Nein. In der Abiturbestenquote zählt ausschließlich die Abiturnote. TMS, Freiwilligendienste, Ausbildungen, Wartezeit oder Motivationsschreiben wirken erst im AdH und in der ZEQ.",
      },
      {
        question: "Muss ich mich für die ABQ gesondert bewerben?",
        answer:
          "Nein. Über hochschulstart.de wirst du automatisch in der ABQ berücksichtigt. Reicht deine Note nicht, läuft die Bewerbung ohne zusätzlichen Schritt in den anderen Quoten weiter.",
      },
      {
        question: "Was passiert mit nicht besetzten ABQ-Plätzen?",
        answer:
          "Sie lösen keine ABQ-interne Warteliste aus, sondern gehen in das Auswahlverfahren der Hochschulen über und werden dort zusätzlich vergeben.",
      },
    ],
  },
  {
    slug: "auswahlverfahren-der-hochschulen",
    term: "Auswahlverfahren der Hochschulen (AdH)",
    category: "Quoten",
    short:
      "Größte Quote (rund 60 %): Jede Hochschule mischt Note, Test und weitere Kriterien selbst.",
    definition:
      "Im Auswahlverfahren der Hochschulen (AdH) werden rund 60 Prozent der Medizin-Studienplätze vergeben – es ist damit die mit Abstand größte Quote. Jede Hochschule legt eigenständig fest, welche Kriterien sie nutzt und wie stark sie Abiturnote, Studierfähigkeitstest und weitere Faktoren gewichtet.",
    seoTitle: "Auswahlverfahren der Hochschulen (AdH) | ncrechner",
    metaDescription:
      "Das AdH vergibt rund 60 % der Medizin-Studienplätze. Jede Uni gewichtet Note, TMS und weitere Kriterien selbst – so verbesserst du hier gezielt deine Chancen.",
    heroImage: "/images/blog/z-adh.jpg",
    heroImageAlt: "Auswahlgespräch als Kriterium im Auswahlverfahren der Hochschulen",
    body: [],
    sections: [
      {
        id: "ueberblick",
        heading: "So funktioniert das AdH",
        paragraphs: [
          "Das AdH ist die größte der drei Quoten und steht zwischen der Abiturbestenquote (rund 30 Prozent) und der Zusätzlichen Eignungsquote (rund 10 Prozent). Vorgeschrieben ist nur ein Rahmen: Jede Hochschule muss mindestens zwei Kriterien heranziehen, darunter eines, das auf der Schulnote beruht.",
        ],
      },
      {
        id: "kriterien",
        heading: "Welche Kriterien zählen",
        paragraphs: [
          "Innerhalb dieses Rahmens ist die Hochschule frei. Zur Auswahl stehen unter anderem die Abiturnote, ein Studierfähigkeitstest wie TMS oder HAM-Nat, eine abgeschlossene Berufsausbildung, ein Freiwilligendienst, teils die Ortspräferenz und an einzelnen Standorten ein Auswahlgespräch.",
        ],
      },
      {
        id: "gewichtung",
        heading: "Gewichtung: Note gegen Test",
        paragraphs: [
          "Wie stark jedes Kriterium zählt, steht in der Zulassungsordnung der jeweiligen Uni. Die Abiturnote wird häufig mit etwa 20 bis 60 Prozent gewichtet. An stark test-orientierten Standorten macht der TMS dagegen 60 bis 100 Prozent der AdH-Wertung aus.",
        ],
      },
      {
        id: "kein-nc",
        heading: "Warum es keinen pauschalen NC gibt",
        paragraphs: [
          "Das hat eine wichtige Folge: Dieselbe Kombination aus Note und Test kann an einer Hochschule für einen Platz reichen und an einer anderen nicht. Einen pauschalen NC fürs AdH gibt es deshalb nicht – jede Uni ist ein eigener Fall.",
        ],
      },
      {
        id: "tms-hebel",
        heading: "TMS als stärkster Hebel",
        paragraphs: [
          "Der wirksamste Hebel, den du selbst in der Hand hast, ist meist der TMS. Ein starkes Ergebnis öffnet gerade an test-orientierten Hochschulen viele Türen und kann eine mittlere Abiturnote spürbar ausgleichen.",
        ],
      },
      {
        id: "bewerbung",
        heading: "Bewerbung und Ortswahl",
        paragraphs: [
          "Eine gesonderte Bewerbung fürs AdH gibt es nicht: Die Auswahl läuft automatisch über hochschulstart.de. Für die Praxis heißt das, pro Wunsch-Uni die Zulassungsordnung zu prüfen und die Ortspräferenzen dorthin zu legen, wo dein Profil zur Gewichtung passt. Die ncrechner App zeigt dir Hochschule für Hochschule, wie deine Werte im jeweiligen AdH stehen.",
        ],
      },
    ],
    related: [
      "abiturbestenquote",
      "zusaetzliche-eignungsquote",
      "tms",
      "hochschulstart",
    ],
    article: {
      label: "AdH verständlich erklärt",
      href: "/ratgeber/zulassung/auswahlverfahren-hochschulen-adh-erklaert/",
    },
    faqs: [
      {
        question: "Wie viele Studienplätze gehen über das AdH?",
        answer:
          "Rund 60 Prozent je Studiengang – das AdH ist die größte der drei Quoten. Die übrigen Plätze teilen sich die Abiturbestenquote (etwa 30 Prozent) und die Zusätzliche Eignungsquote (etwa 10 Prozent).",
      },
      {
        question: "Warum unterscheiden sich die AdH-Kriterien je Uni?",
        answer:
          "Weil jede Hochschule im AdH selbst festlegt, welche Kriterien sie nutzt und wie sie sie gewichtet. Vorgeschrieben sind nur mindestens zwei Kriterien, darunter ein schulnotenbasiertes. Deshalb kann dasselbe Profil an einer Uni reichen und an einer anderen nicht.",
      },
      {
        question: "Wie stark zählt die Abiturnote im AdH?",
        answer:
          "Je nach Hochschule meist zwischen etwa 20 und 60 Prozent. An stark test-orientierten Standorten kann der TMS dagegen 60 bis 100 Prozent der Wertung ausmachen und die Note weit in den Hintergrund rücken.",
      },
      {
        question: "Ist der TMS fürs AdH Pflicht?",
        answer:
          "Nicht an jeder Uni, aber er ist der wirksamste aktiv beeinflussbare Hebel. Gerade an test-orientierten Hochschulen entscheidet der TMS im AdH stark über die Chancen.",
      },
    ],
  },
  {
    slug: "zusaetzliche-eignungsquote",
    term: "Zusätzliche Eignungsquote (ZEQ)",
    category: "Quoten",
    short:
      "Notenunabhängige Quote (rund 10 %) für Tests, Ausbildungen, Dienste und Wettbewerbe.",
    definition:
      "Die Zusätzliche Eignungsquote (ZEQ) vergibt rund 10 Prozent der Medizin-Studienplätze – und zwar völlig unabhängig von der Abiturnote, die hier keine Rolle spielt. Gewertet werden stattdessen Studierfähigkeitstests, abgeschlossene Ausbildungen, Dienste, Berufserfahrung und anerkannte Wettbewerbe.",
    seoTitle: "Zusätzliche Eignungsquote (ZEQ) erklärt | ncrechner",
    metaDescription:
      "Die ZEQ vergibt rund 10 % der Medizin-Studienplätze ganz ohne Abiturnote. Es zählen TMS, Ausbildung, Dienst und Wettbewerbe – ideal bei mittlerem Abitur.",
    heroImage: "/images/blog/z-zeq.jpg",
    heroImageAlt: "Abgeschlossene Ausbildung und Test als Kriterien der Zusätzlichen Eignungsquote",
    body: [],
    sections: [
      {
        id: "ueberblick",
        heading: "Was die ZEQ ist",
        paragraphs: [
          "Die ZEQ ergänzt die Abiturbestenquote (rund 30 Prozent) und das Auswahlverfahren der Hochschulen (rund 60 Prozent). Ihr Sinn ist klar umrissen: Sie öffnet Bewerbern mit einer mittleren oder schwächeren Note, aber starken Zusatzqualifikationen einen eigenen Weg.",
        ],
      },
      {
        id: "notenunabhaengig",
        heading: "Notenunabhängig ausgewählt",
        paragraphs: [
          "Anders als in der Abiturbestenquote spielt die Note in der ZEQ überhaupt keine Rolle. Gewertet werden ausschließlich notenunabhängige Kriterien – genau das, was du nach dem Abitur noch aufbauen kannst.",
        ],
      },
      {
        id: "kriterien",
        heading: "Welche Kriterien zählen",
        paragraphs: [
          "Dazu zählen je nach Hochschule ein Studierfähigkeitstest wie TMS oder HAM-Nat, eine abgeschlossene medizinnahe Berufsausbildung, ein Freiwilligendienst oder einschlägige Berufserfahrung, anerkannte Wettbewerbserfolge und an einzelnen Standorten strukturierte Auswahlgespräche.",
        ],
      },
      {
        id: "gewichtung",
        heading: "Gewichtung je Hochschule",
        paragraphs: [
          "Wie diese Kriterien gewichtet werden, entscheidet jede Hochschule selbst. An mehreren Standorten zählt allein der TMS zu 100 Prozent, andere gewichten eine abgeschlossene Ausbildung besonders hoch, wieder andere setzen stark auf einen naturwissenschaftlichen Test oder ein Auswahlgespräch.",
        ],
      },
      {
        id: "fuer-wen",
        heading: "Für wen sich die ZEQ lohnt",
        paragraphs: [
          "Am meisten profitieren Bewerber mit durchschnittlichem Abitur und einem klaren Zusatzprofil – etwa gutem TMS plus abgeschlossener Pflegeausbildung. Diese Kombination gehört zu den stärksten Profilen überhaupt.",
        ],
      },
      {
        id: "bewerbung",
        heading: "Bewerbung",
        paragraphs: [
          "Eine separate ZEQ-Bewerbung gibt es nicht: Über hochschulstart.de wirst du automatisch berücksichtigt. Welche Kriterien an deiner Wunsch-Uni zählen, steht in deren Zulassungsordnung. Mit der ncrechner App erkennst du, an welchen Hochschulen dein ZEQ-Profil reicht und welcher Baustein dir dort die meisten Punkte bringt.",
        ],
      },
    ],
    related: [
      "auswahlverfahren-der-hochschulen",
      "abiturbestenquote",
      "tms",
      "berufsausbildung",
    ],
    article: {
      label: "Zusätzliche Eignungsquote (ZEQ)",
      href: "/ratgeber/zulassung/zusaetzliche-eignungsquote-zeq-erklaert/",
    },
    faqs: [
      {
        question: "Brauche ich für die ZEQ ein gutes Abitur?",
        answer:
          "Nein. Die Zusätzliche Eignungsquote ist notenunabhängig – die Abiturnote spielt keine Rolle. Es zählen Tests, Ausbildungen, Dienste und Wettbewerbe, ideal um einen mittleren Abischnitt auszugleichen.",
      },
      {
        question: "Wie viele Studienplätze gehen über die ZEQ?",
        answer:
          "Rund 10 Prozent je Studiengang. Damit ist die ZEQ die kleinste der drei Quoten, für Bewerber mit starkem Zusatzprofil aber oft der entscheidende Weg.",
      },
      {
        question: "Welche Kriterien zählen in der ZEQ?",
        answer:
          "Je nach Hochschule TMS oder HAM-Nat, eine abgeschlossene medizinnahe Ausbildung, ein Dienst oder Berufserfahrung, anerkannte Wettbewerbe und teils Auswahlgespräche. Die Gewichtung legt jede Uni selbst fest.",
      },
      {
        question: "Für wen lohnt sich die ZEQ am meisten?",
        answer:
          "Für Bewerber mit mittlerem oder schwächerem Abitur und einer starken Zusatzqualifikation. Besonders wirksam ist die Kombination aus gutem TMS und abgeschlossener medizinnaher Ausbildung.",
      },
    ],
  },
  {
    slug: "numerus-clausus",
    term: "Numerus Clausus (NC)",
    category: "Grundbegriffe",
    short: "Kein festes Notenlimit, sondern das Ergebnis eines Auswahlverfahrens.",
    definition:
      "Der Numerus Clausus ist keine vorab festgelegte Eintrittsnote, sondern die Auswahlgrenze, ab der ein Studiengang voll ist. Er entsteht erst, nachdem alle Bewerbungen sortiert und die Plätze vergeben sind – er ist damit ein Rückblick, keine Eintrittskarte.",
    seoTitle: "Numerus Clausus (NC) Medizin einfach erklärt | ncrechner",
    metaDescription:
      "Numerus Clausus erklärt: Warum der NC kein festes Limit ist, wie er entsteht, welche Werte typisch sind, wie die drei Quoten wirken und wie du deine Chancen.",
    heroImage: "/images/blog/medizin-zulassung-guide.jpg",
    heroImageAlt: "Abiturnote und Ranglisten als Grundlage des Numerus Clausus",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was der NC wirklich ist",
        paragraphs: [
          "Viele lesen den NC als feste Hürde: „NC 1,0, also brauche ich 1,0.“ Tatsächlich ist der NC nur die Note der zuletzt zugelassenen Person einer vergangenen Runde. Er wird nicht vorher festgelegt, sondern ergibt sich als Nebenprodukt der Vergabe.",
          "Alte NC-Werte zeigen einen Trend, garantieren aber nichts für das kommende Semester.",
        ],
      },
      {
        id: "entstehung",
        heading: "Wie der NC entsteht",
        paragraphs: [
          "Der NC ergibt sich aus Angebot und Nachfrage: aus der Zahl der Plätze und der Zahl und Stärke der Bewerbungen. Weil sich die Nachfrage jedes Semester ändert, schwankt auch der NC.",
          "Typische Werte in der Abiturbestenquote liegen für Humanmedizin bei etwa 1,0 bis 1,2, für Tiermedizin etwas höher. Einen bundesweit einheitlichen Wert gibt es nicht.",
        ],
      },
      {
        id: "quoten",
        heading: "NC und die drei Quoten",
        paragraphs: [
          "Der klassische NC-Wert betrifft vor allem die Abiturbestenquote (rund 30 Prozent). Daneben werden Plätze über das Auswahlverfahren der Hochschulen (rund 60 Prozent) und die Zusätzliche Eignungsquote (rund 10 Prozent) vergeben.",
          "In diesen beiden Quoten zählt die Note nur als ein Baustein neben Test, Dienst und Ausbildung.",
        ],
      },
      {
        id: "chancen",
        heading: "Chancen jenseits des NC verbessern",
        paragraphs: [
          "Ein NC von 1,0 heißt nicht, dass du zwingend 1,0 brauchst. Ein starker TMS, ein Dienst oder eine Ausbildung können in AdH und ZEQ einen schwächeren Schnitt ausgleichen. Eine reine Wartezeitquote gibt es seit 2020 nicht mehr.",
          "Sinnvoller als der bloße NC ist der Blick auf Grenzrang und Auswahlgrenzen mehrerer Runden. Die ncrechner App ordnet deine Werte genau dort ein.",
        ],
      },
    ],
    related: ["grenzrang", "abiturbestenquote"],
    article: {
      label: "Numerus Clausus berechnen",
      href: "/ratgeber/zulassungsrechner/numerus-clausus-berechnung-verstehen/",
    },
    faqs: [
      {
        question: "Ist der NC jedes Jahr gleich?",
        answer:
          "Nein. Der NC entsteht neu nach jeder Vergaberunde und hängt von Bewerberzahl, Profilstärke und Platzangebot ab. Ein einzelner Wert ist deshalb nur eine Momentaufnahme.",
      },
      {
        question: "Brauche ich bei NC 1,0 wirklich eine 1,0?",
        answer:
          "Nicht unbedingt. Der NC gilt für die Abiturbestenquote. In AdH und ZEQ zählen zusätzlich Tests und Erfahrungen, mit denen sich ein schwächerer Schnitt ausgleichen lässt.",
      },
      {
        question: "Gibt es einen bundesweit einheitlichen NC?",
        answer:
          "Nein. Weil in den Bundesländern getrennte Ranglisten gebildet werden und jede Hochschule eigene Kriterien hat, unterscheiden sich die Werte je Land, Uni und Quote.",
      },
    ],
  },
  {
    slug: "grenzrang",
    term: "Grenzrang",
    category: "Grundbegriffe",
    short: "Die Position der zuletzt zugelassenen Person am Ende einer Runde.",
    definition:
      "Der Grenzrang ist die Ranglistenposition der zuletzt zugelassenen Person in einer bestimmten Quote und Hochschule – am Ende einer Vergaberunde inklusive allem Nachrücken. Weil er die Verrechnung aller Kriterien einbezieht, ist er in AdH und ZEQ oft aussagekräftiger als der reine NC.",
    seoTitle: "Grenzrang verstehen und für die Bewerbung nutzen | ncrechner",
    metaDescription:
      "Grenzrang erklärt: Wie er je Quote und Hochschule entsteht, warum er jedes Semester schwankt, wie er sich vom NC unterscheidet und wie du ihn für deine.",
    heroImage: "/images/blog/studienplatzvergabe-medizin.jpg",
    heroImageAlt: "Ranglisten der Studienplatzvergabe als Grundlage des Grenzrangs",
    body: [],
    sections: [
      {
        id: "definition",
        heading: "Was der Grenzrang beschreibt",
        paragraphs: [
          "Der Grenzrang markiert die letzte Position auf der Rangliste, die in einer Runde noch einen Platz bekommen hat. Wer auf oder über diesem Rang steht, erhält ein Angebot; wer darunter liegt, geht zunächst leer aus und hofft aufs Nachrücken.",
          "In der Abiturbestenquote entspricht die Grenze praktisch einem Notenwert. In AdH und ZEQ entsteht sie aus den verrechneten Punktzahlen aller Kriterien.",
        ],
      },
      {
        id: "je-quote",
        heading: "Eigener Grenzrang je Quote und Uni",
        paragraphs: [
          "Jede Hochschule führt für jede Quote eine eigene Rangliste. Dieselbe Bewerbung kann an einer Uni über der Grenze liegen und an einer anderen darunter – einen bundesweit einheitlichen Grenzrang gibt es nicht.",
          "Deshalb lohnt der Vergleich mehrerer Standorte statt der Blick auf einen einzelnen Wert.",
        ],
      },
      {
        id: "schwankung",
        heading: "Warum er jedes Semester schwankt",
        paragraphs: [
          "Der Grenzrang ist keine feste Größe. Er hängt von der Zahl der Plätze, der Bewerberzahl, der Stärke der Profile und der Beliebtheit des Standorts ab – all das ändert sich von Semester zu Semester.",
          "Vorab lässt sich der genaue Wert daher nie sicher vorhersagen, nur als Korridor abschätzen.",
        ],
      },
      {
        id: "nutzen",
        heading: "So nutzt du den Grenzrang",
        paragraphs: [
          "Vergleiche deinen errechneten Rang mit den historischen Grenzrängen mehrerer Runden und denke in Bereichen, nicht in festen Zahlen. Liegst du klar darüber, ist der Platz realistisch; knapp darunter bleibt es eine Wette aufs Nachrücken.",
          "Die ncrechner App ordnet deine Werte automatisch in die historischen Grenzränge jeder Hochschule und Quote ein.",
        ],
      },
    ],
    related: ["numerus-clausus", "nachruecken", "auswahlverfahren-der-hochschulen"],
    article: {
      label: "Auswahlgrenzen Medizin: die NC-Tabellen richtig lesen",
      href: "/ratgeber/zulassungsrechner/auswahlgrenzen-medizin-tabellen-lesen/",
    },
    faqs: [
      {
        question: "Ist der Grenzrang dasselbe wie der NC?",
        answer:
          "Nicht ganz. Der NC ist der Oberbegriff für die Zulassungsbeschränkung, der Grenzrang die konkrete Position nach Verrechnung aller Kriterien. In AdH und ZEQ ist der Rang die genauere Kennzahl.",
      },
      {
        question: "Warum ändert sich der Grenzrang jedes Semester?",
        answer:
          "Weil Platzangebot, Bewerberzahl und Profilstärke schwanken. Ein einzelner Wert ist nur eine Momentaufnahme – aussagekräftig wird erst der Vergleich mehrerer Runden.",
      },
      {
        question: "Wie sicher ist ein Platz über dem Grenzrang?",
        answer:
          "Je deutlicher du über dem historischen Grenzrang liegst, desto sicherer. Ein knapper Vorsprung bleibt riskant, weil der Wert schwankt. Plane deshalb immer mehrere Hochschulen ein.",
      },
    ],
  },
  {
    slug: "hochschulstart",
    term: "hochschulstart.de",
    category: "Bewerbung",
    short: "Die zentrale Vergabestelle für bundesweit zulassungsbeschränkte Fächer.",
    definition:
      "hochschulstart.de ist die zentrale Vergabestelle der Stiftung für Hochschulzulassung. Über ihr Dialogorientiertes Serviceverfahren (DoSV) werden Human-, Zahn- und Tiermedizin sowie Pharmazie vergeben – nicht direkt bei den Hochschulen, sondern koordiniert über eine gemeinsame Plattform.",
    seoTitle: "hochschulstart.de: zentrale Vergabe erklärt | ncrechner",
    metaDescription:
      "hochschulstart.de erklärt: Welche Fächer zentral vergeben werden, wie das DoSV und die Registrierung ablaufen, welche Fristen und Quoten gelten und welche.",
    heroImage: "/images/blog/b-hochschulstart-anleitung.jpg",
    heroImageAlt: "Bewerber am Laptop im Portal von hochschulstart.de",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was hochschulstart.de ist",
        paragraphs: [
          "hochschulstart.de ist die bundesweite Stelle, die zulassungsbeschränkte Studienfächer transparent auf alle Hochschulen verteilt. Betrieben wird sie von der Stiftung für Hochschulzulassung.",
          "Zentral vergeben werden Humanmedizin, Zahnmedizin, Tiermedizin und Pharmazie. Für diese Fächer führt kein Weg an der Plattform vorbei.",
        ],
      },
      {
        id: "registrierung",
        heading: "Registrierung und AntOn",
        paragraphs: [
          "Zuerst legst du im DoSV-Portal ein Konto an und erhältst eine Bewerber-ID (BID) und eine Authentifizierungsnummer (BAN). Damit meldest du dich im Bewerbungsportal AntOn an, in das deine Daten automatisch übernommen werden.",
          "Dort wählst und priorisierst du bis zu zwölf Studienwünsche. Einige Unterlagen wie das unterschriebene Formular oder beglaubigte Zeugniskopien musst du zusätzlich einreichen.",
        ],
      },
      {
        id: "fristen-quoten",
        heading: "Fristen und Quoten",
        paragraphs: [
          "Die Fristen sind für alle gleich und hart: Wer sie reißt, verliert das ganze Verfahren. Für Alt- und Neu-Abiturienten gelten teils unterschiedliche Termine, TMS-Ergebnisse lassen sich meist bis Mitte Juli nachreichen.",
          "Nach den Vorabquoten werden die Plätze über drei Quoten vergeben: Abiturbestenquote (rund 30 Prozent), Zusätzliche Eignungsquote (rund 10 Prozent) und Auswahlverfahren der Hochschulen (rund 60 Prozent).",
        ],
      },
      {
        id: "fehler",
        heading: "Häufige Fehler vermeiden",
        paragraphs: [
          "Eine reine Wartezeitquote gibt es seit 2020 nicht mehr. Der TMS ist freiwillig, stärkt aber AdH und ZEQ deutlich. Der häufigste Fehler ist eine unüberlegte Priorisierung oder das voreilige Annehmen eines Angebots.",
          "Mit der ncrechner App planst du deine Ortspräferenzen vorab so, dass sie zu deinen realistischen Chancen passen.",
        ],
      },
    ],
    related: ["koordinierungsphase", "koordinierungsregeln", "ortspraeferenz"],
    article: {
      label: "hochschulstart & DoSV: Registrierung",
      href: "/ratgeber/bewerbung/hochschulstart-registrierung-dosv/",
    },
    faqs: [
      {
        question: "Bewerbe ich mich direkt bei der Uni?",
        answer:
          "Für Human-, Zahn-, Tiermedizin und Pharmazie nicht. Diese Fächer laufen zentral über hochschulstart.de. Nur einzelne Schritte, etwa bei privaten Hochschulen, können separat sein.",
      },
      {
        question: "Wie viele Studienwünsche kann ich angeben?",
        answer:
          "Bis zu zwölf, die du nach Priorität ordnest. Die Reihenfolge steuert, welches Angebot du am Ende bekommst – nicht, ob du an einer bestimmten Uni überhaupt zugelassen wirst.",
      },
      {
        question: "Brauche ich für hochschulstart.de einen TMS?",
        answer:
          "Nein, der TMS ist freiwillig. Fehlt er, wirst du nicht abgelehnt. Ein gutes Ergebnis stärkt aber deine Chancen in der ZEQ und im AdH deutlich.",
      },
    ],
  },
  {
    slug: "ortspraeferenz",
    term: "Ortspräferenz",
    category: "Bewerbung",
    short: "Die Reihenfolge, in der du deine Wunsch-Hochschulen angibst.",
    definition:
      "Die Ortspräferenz ist die Rangfolge deiner Wunsch-Hochschulen im Bewerbungsportal von hochschulstart.de. Sie steuert, an welchen Standorten du in welcher Reihenfolge berücksichtigt wirst, und ist damit einer der am meisten unterschätzten strategischen Hebel der Bewerbung.",
    seoTitle: "Ortspräferenz im Medizin-Bewerbungsverfahren | ncrechner",
    metaDescription:
      "Ortspräferenz erklärt: Wie die Reihenfolge deiner Wunsch-Unis funktioniert, warum manche Unis nur die erste Präferenz werten und wie du klug priorisierst.",
    heroImage: "/images/blog/b-ortspraeferenzen.jpg",
    heroImageAlt: "Deutschlandkarte mit Hochschulstandorten für die Ortspräferenz",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was die Ortspräferenz ist",
        paragraphs: [
          "Bei der Bewerbung gibst du deine Wunsch-Hochschulen in einer festen Reihenfolge an. Diese Rangfolge fließt direkt ins Vergabeverfahren ein und beeinflusst deine Chancen deutlich.",
          "Sie ist Teil deiner priorisierten Studienwünsche über hochschulstart.de.",
        ],
      },
      {
        id: "erste-praeferenz",
        heading: "Warum die erste Präferenz zählt",
        paragraphs: [
          "Manche Hochschulen berücksichtigen dich in ihrem Auswahlverfahren nur, wenn du sie als erste Präferenz angegeben hast, andere nur unter den ersten Plätzen deiner Liste. Wer eine solche Uni zu weit hinten einordnet, fällt dort trotz guter Werte heraus.",
          "Prüfe deshalb pro Wunsch-Uni, ob sie eine bestimmte Präferenzposition verlangt.",
        ],
      },
      {
        id: "quoten",
        heading: "Zusammenspiel mit den Quoten",
        paragraphs: [
          "Neben der Präferenzregel gewichten Hochschulen weitere Kriterien wie TMS, Note oder Auswahlgespräch. Die Ortspräferenz wirkt also zusammen mit den übrigen Faktoren des Auswahlverfahrens.",
          "Deine Reihenfolge entscheidet vor allem über das Wohin, nicht allein über das Ob.",
        ],
      },
      {
        id: "strategie",
        heading: "Strategie und typische Fehler",
        paragraphs: [
          "Eine kluge Mischung aus Wunsch- und realistischen Standorten schlägt die reine Prestige-Liste. Lege deine Präferenzen so, dass ein Teil rechnerisch klar in deiner Reichweite liegt.",
          "Der häufigste Fehler ist eine unbedachte Reihenfolge, die trotz starker Werte zur Absage führt. Mit der ncrechner App siehst du, an welchen Standorten dein Profil reicht.",
        ],
      },
    ],
    related: ["hochschulstart", "priorisierung", "grenzrang"],
    article: {
      label: "Ortspräferenzen-Strategie",
      href: "/ratgeber/bewerbung/ortspraeferenzen-strategie/",
    },
    faqs: [
      {
        question: "Muss ich eine Uni als erste Präferenz angeben?",
        answer:
          "An einigen Hochschulen ja: Sie werten dich nur, wenn du sie an erster oder sehr weit vorderer Stelle führst. Prüfe die Vorgaben deiner Wunsch-Unis, bevor du deine Reihenfolge festlegst.",
      },
      {
        question: "Beeinflusst die Reihenfolge, ob ich zugelassen werde?",
        answer:
          "Vor allem beeinflusst sie, an welchem Standort du einen Platz bekommst. An Unis mit Präferenzregeln kann sie aber auch darüber entscheiden, ob du dort überhaupt berücksichtigt wirst.",
      },
      {
        question: "Wie viele Wunsch-Standorte kann ich angeben?",
        answer:
          "Über hochschulstart.de ordnest du deine Studienwünsche nach Priorität. Nutze die Plätze bewusst und mische sichere mit ehrgeizigen Standorten, statt nur die bekanntesten Unis aufzulisten.",
      },
    ],
  },
  {
    slug: "koordinierungsphase",
    term: "Koordinierungsphase",
    category: "Bewerbung",
    short: "Die entscheidende Phase, in der Angebote verteilt und angenommen werden.",
    definition:
      "Die Koordinierungsphase ist der Abschnitt im DoSV, in dem hochschulstart.de über mehrere Wochen Zulassungsangebote verteilt. Das System gleicht deine priorisierten Wünsche mit den Ranglisten der Hochschulen ab. Hier fallen die eigentlichen Entscheidungen – in einem engen Zeitfenster mit festen Fristen.",
    seoTitle: "Koordinierungsphase im DoSV verstehen | ncrechner",
    metaDescription:
      "Koordinierungsphase erklärt: Wie Angebote verteilt und angenommen werden, warum ein angenommenes Angebot alles beendet, wie das Nachrücken funktioniert.",
    heroImage: "/images/blog/b-koordinierung.jpg",
    heroImageAlt: "Zeitplan und Angebote in der Koordinierungsphase des Medizin-Bewerbungsverfahrens",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was in der Koordinierungsphase passiert",
        paragraphs: [
          "Nach dem Bewerbungsschluss ordnen die Hochschulen ihre Bewerber nach ihren Kriterien. Das System erzeugt daraus – kombiniert mit deiner Prioritätenliste – laufend Angebote. Die Phase läuft über mehrere Wochen.",
          "Die genauen Termine ändern sich jedes Verfahren, deshalb lohnt der Blick in den Kalender von hochschulstart.de.",
        ],
      },
      {
        id: "angebote",
        heading: "Angebote annehmen oder warten",
        paragraphs: [
          "Du kannst ein Angebot annehmen oder auf ein höher priorisiertes warten. Wichtig ist die zentrale Regel: Ein angenommenes Angebot beendet das Verfahren sofort, alle niedriger priorisierten Wünsche verfallen unwiderruflich.",
          "Solange du ein Angebot nur stehen lässt, bleibst du für alle höher priorisierten Wünsche im Rennen.",
        ],
      },
      {
        id: "nachruecken",
        heading: "Nachrücken innerhalb der Phase",
        paragraphs: [
          "Frei werdende Plätze rücken automatisch an die nächstplatzierten Bewerber weiter. Ein niedriger Rang zu Beginn ist deshalb kein endgültiges Aus – die Listen bewegen sich über die Phase hinweg.",
          "Voraussetzung ist, dass du im Verfahren bleibst und noch kein Angebot angenommen hast.",
        ],
      },
      {
        id: "strategie",
        heading: "Fristen und Strategie",
        paragraphs: [
          "Reagiere auf Angebote innerhalb der genannten Fristen, sonst riskierst du den Ausschluss. Nimm ein sicheres Angebot nicht vorschnell an, wenn ein höher priorisierter Wunsch noch erreichbar ist.",
          "Wer bis zum Ende der Phase kein Angebot hat, kann es anschließend über das Losverfahren versuchen.",
        ],
      },
    ],
    related: ["koordinierungsregeln", "nachruecken", "losverfahren"],
    article: {
      label: "Koordinierungsphase verstehen",
      href: "/ratgeber/bewerbung/koordinierungsphase-verstehen/",
    },
    faqs: [
      {
        question: "Was passiert, wenn ich ein Angebot annehme?",
        answer:
          "Das Verfahren endet für dich sofort. Alle niedriger priorisierten Wünsche verfallen unwiderruflich. Nimm ein Angebot deshalb nur an, wenn du keinen höher priorisierten Platz mehr erreichen willst.",
      },
      {
        question: "Ist ein niedriger Rang zu Beginn das Aus?",
        answer:
          "Nein. Durch Absagen und Nachrücken bewegen sich die Listen über die Phase. Ein knapp verpasster Rang kann sich noch in einen Platz verwandeln, solange du im Verfahren bleibst.",
      },
      {
        question: "Was, wenn ich am Ende kein Angebot habe?",
        answer:
          "Dann bleibt das Losverfahren um Restplätze. Die Chancen sind gering, der Aufwand aber klein – für viele die letzte Möglichkeit im selben Bewerbungsjahr.",
      },
    ],
  },
  {
    slug: "nachruecken",
    term: "Nachrücken",
    category: "Bewerbung",
    short: "Das Aufrücken auf frei werdende Plätze nach Absagen.",
    definition:
      "Nachrücken bedeutet, dass frei werdende Studienplätze in weiteren Runden an nachfolgende Bewerber vergeben werden. Weil viele zunächst mehrere Angebote sammeln und wieder absagen, bewegen sich die Grenzwerte im Verlauf des Verfahrens spürbar nach unten – ein knapp verpasster Rang kann so noch zum Platz werden.",
    seoTitle: "Nachrücken im Medizin-Verfahren erklärt | ncrechner",
    metaDescription:
      "Nachrücken erklärt: Wie frei werdende Plätze weiter vergeben werden, warum der erste Verfahrenstag wenig aussagt, was du dafür tun musst und wie es sich vom.",
    heroImage: "/images/blog/b-nachruecken.jpg",
    heroImageAlt: "Ranglisten, die sich durch Nachrücken nach unten verschieben",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was Nachrücken bedeutet",
        paragraphs: [
          "Sagt jemand einen Platz ab oder nimmt ein anderes Angebot an, wird der frei gewordene Platz an die nächstplatzierte Person weitergegeben. Dieser Prozess läuft über mehrere Runden innerhalb der Koordinierungsphase.",
          "Weil zu Beginn viele mehrere Optionen haben, ist die Bewegung im Verlauf oft erheblich.",
        ],
      },
      {
        id: "grenzwerte",
        heading: "Warum der erste Tag wenig aussagt",
        paragraphs: [
          "Am ersten Verfahrenstag wirken die Grenzwerte streng, weil noch niemand nachgerückt ist. Erst der Endstand nach allen Nachrückrunden zeigt, wo die Liste wirklich stand.",
          "Vergleiche deine Position deshalb immer mit Endständen, nicht mit dem Startwert einer Runde.",
        ],
      },
      {
        id: "voraussetzung",
        heading: "Was du dafür tun musst",
        paragraphs: [
          "Um nachzurücken, musst du im Verfahren bleiben und darfst noch kein Angebot angenommen haben. Ein angenommenes Angebot beendet die Teilnahme sofort.",
          "Behalte dein Konto und die Fristen im Blick, damit du auf ein nachrückendes Angebot rechtzeitig reagierst.",
        ],
      },
      {
        id: "vs-los",
        heading: "Nachrücken oder Losverfahren?",
        paragraphs: [
          "Nachrücken passiert automatisch im laufenden Verfahren und folgt der Rangliste. Das Losverfahren kommt erst danach und vergibt Restplätze per Zufall.",
          "Ein niedriger Rang zu Beginn ist also kein Aus – über das Nachrücken kann sich noch ein Platz ergeben.",
        ],
      },
    ],
    related: ["koordinierungsphase", "grenzrang", "losverfahren"],
    article: {
      label: "Koordiniertes Nachrücken",
      href: "/ratgeber/bewerbung/koordiniertes-nachruecken/",
    },
    faqs: [
      {
        question: "Muss ich mich fürs Nachrücken extra bewerben?",
        answer:
          "Nein. Nachrücken läuft automatisch im laufenden Verfahren, solange du im Prozess bist und noch kein Angebot angenommen hast. Behalte nur Angebote und Fristen aktiv im Blick.",
      },
      {
        question: "Ist ein niedriger Rang das Aus?",
        answer:
          "Nicht unbedingt. Durch Absagen bewegen sich die Listen über die Phase nach unten. Ein knapp verpasster Rang kann sich durch Nachrücken noch in einen Studienplatz verwandeln.",
      },
      {
        question: "Was ist der Unterschied zum Losverfahren?",
        answer:
          "Nachrücken folgt der Rangliste im laufenden Verfahren. Das Losverfahren kommt erst danach und vergibt übrig gebliebene Restplätze per Zufall, unabhängig von Note oder Test.",
      },
    ],
  },
  {
    slug: "tms",
    term: "Test für medizinische Studiengänge (TMS)",
    category: "Tests",
    short: "Der wichtigste Studierfähigkeitstest – anerkannt an den meisten Unis.",
    definition:
      "Der TMS ist ein freiwilliger Studierfähigkeitstest, der an vielen Hochschulen in AdH und ZEQ hoch gewichtet wird. Er prüft kein medizinisches Vorwissen, sondern kognitive Fähigkeiten in mehreren Untertests. Ein starkes Ergebnis kann einen mittleren Abischnitt an zahlreichen Standorten ausgleichen.",
    seoTitle: "TMS: Test für medizinische Studiengänge | ncrechner",
    metaDescription:
      "TMS erklärt: der wichtigste Studierfähigkeitstest, was er prüft, wie er aufgebaut ist, wie stark er in AdH und ZEQ zählt und wie du dich sinnvoll vorbereitest.",
    heroImage: "/images/blog/t-untertests-ueberblick.jpg",
    heroImageAlt: "Aufgabentypen und Untertests des TMS im Überblick",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was der TMS ist",
        paragraphs: [
          "Der TMS ist ein freiwilliger Test, der die Bewerbung fürs Medizinstudium unterstützt. Er misst kognitive Fähigkeiten, nicht auswendig gelerntes Fachwissen, und lässt sich deshalb gezielt trainieren.",
          "Weil ihn die meisten Hochschulen anerkennen, ist er für die allermeisten Bewerber die erste Wahl unter den Studierfähigkeitstests.",
        ],
      },
      {
        id: "aufbau",
        heading: "Aufbau des Tests",
        paragraphs: [
          "Der TMS besteht aus mehreren Untertests, die unter anderem naturwissenschaftliches Grundverständnis, Textverständnis, Muster- und Figurenzuordnung, quantitative Aufgaben sowie Konzentration und Gedächtnis prüfen.",
          "Er dauert mehrere Stunden und wird an festen Terminen im Jahr geschrieben.",
        ],
      },
      {
        id: "gewichtung",
        heading: "Wie stark der TMS zählt",
        paragraphs: [
          "In vielen Auswahlverfahren wird der TMS hoch gewichtet, an test-orientierten Standorten macht er einen Großteil der Wertung aus. Das Ergebnis wird oft als Prozentrang angegeben, der sich gut mit Auswahlgrenzen vergleichen lässt.",
          "Damit ist er der wirksamste Hebel, den du unabhängig von deiner Note in der Hand hast.",
        ],
      },
      {
        id: "vorbereitung",
        heading: "Vorbereitung",
        paragraphs: [
          "Weil der TMS trainierbar ist, lohnt ein strukturierter Lernplan über mehrere Monate mit Übungsaufgaben und Simulationen unter Zeitdruck. Jede Teilnahme sollte gut vorbereitet sein.",
          "Mit der ncrechner App siehst du, welcher Prozentrang an deinen Wunsch-Hochschulen reicht und wie stark der TMS dort gewichtet wird.",
        ],
      },
    ],
    related: ["ham-nat", "studierfaehigkeitstest", "auswahlverfahren-der-hochschulen"],
    article: {
      label: "TMS: der große Guide",
      href: "/ratgeber/zulassung/tms-test-medizinische-studiengaenge-guide/",
    },
    faqs: [
      {
        question: "Ist der TMS Pflicht?",
        answer:
          "Nein, der TMS ist freiwillig. Aber weil ihn viele Hochschulen hoch gewichten, verbessert ein gutes Ergebnis die Chancen deutlich, gerade bei einem mittleren Abitur.",
      },
      {
        question: "Was prüft der TMS?",
        answer:
          "Kognitive Fähigkeiten in mehreren Untertests, etwa naturwissenschaftliches Grundverständnis, Textverständnis, Muster- und Figurenaufgaben sowie Konzentration. Medizinisches Vorwissen wird nicht abgefragt.",
      },
      {
        question: "Wie oft kann ich den TMS schreiben?",
        answer:
          "Der TMS kann grundsätzlich mehrfach abgelegt werden. Da jede Teilnahme Vorbereitung und Organisation verlangt, lohnt sich von Anfang an ein strukturierter Lernplan.",
      },
    ],
  },
  {
    slug: "ham-nat",
    term: "HAM-Nat",
    category: "Tests",
    short: "Naturwissenschaftlicher Wissenstest einzelner Hochschulen wie Hamburg.",
    definition:
      "Der HAM-Nat ist ein naturwissenschaftlicher Wissenstest, den einzelne Hochschulen wie Hamburg, Magdeburg und Greifswald in ihrem Auswahlverfahren einsetzen. Anders als der TMS prüft er gelerntes Fachwissen in Biologie, Chemie, Physik und Mathematik, ergänzt um einen Logikteil.",
    seoTitle: "HAM-Nat: naturwissenschaftlicher Test erklärt | ncrechner",
    metaDescription:
      "HAM-Nat erklärt: Welche Unis ihn nutzen, was er prüft, wie er aufgebaut ist, wie stark er zählt und wie er sich vom TMS unterscheidet – plus Vorbereitung.",
    heroImage: "/images/blog/z-ham-nat.jpg",
    heroImageAlt: "Naturwissenschaftliche Aufgaben als Inhalt des HAM-Nat",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was der HAM-Nat ist",
        paragraphs: [
          "Der HAM-Nat prüft naturwissenschaftliches Grundwissen und logisches Denken. Er wird für Humanmedizin an mehreren Standorten eingesetzt, für Zahn- und Pharmazie an einzelnen. Anders als der TMS ist er ein Wissenstest, kein reiner Fähigkeitstest.",
          "Weil er Stoff abfragt, bereitet man sich wie auf eine Klausur vor – mit einem klaren Themenkatalog.",
        ],
      },
      {
        id: "aufbau",
        heading: "Aufbau und Ablauf",
        paragraphs: [
          "Der Test besteht aus Multiple-Choice-Aufgaben über rund zweieinhalb bis drei Stunden: überwiegend Naturwissenschaften aus Biologie, Chemie, Physik und Mathematik, ergänzt um einen kleineren Logikteil.",
          "Er findet in der Regel einmal jährlich statt, meist im Frühjahr, mit einer Anmeldung einige Monate vorher.",
        ],
      },
      {
        id: "gewichtung",
        heading: "Wie stark der HAM-Nat zählt",
        paragraphs: [
          "An den nutzenden Hochschulen kann der HAM-Nat einen erheblichen Teil der Auswahlentscheidung ausmachen. Ein starkes Ergebnis kann dort eine mittlere Abiturnote deutlich aufwerten.",
          "Er wirkt nur an den Standorten, die ihn einsetzen – für die Breite ist der TMS wichtiger.",
        ],
      },
      {
        id: "vs-tms",
        heading: "HAM-Nat oder TMS?",
        paragraphs: [
          "Der TMS ist breiter anerkannt und öffnet weit mehr Standorte. Der HAM-Nat lohnt gezielt, wenn eine der nutzenden Hochschulen dein Wunschort ist. Beide lassen sich kombinieren.",
          "In der ncrechner App siehst du, an welchen deiner Wunsch-Unis der HAM-Nat überhaupt gefragt ist.",
        ],
      },
    ],
    related: ["tms", "studierfaehigkeitstest", "auswahlverfahren-der-hochschulen"],
    article: {
      label: "HAM-Nat erklärt",
      href: "/ratgeber/zulassung/ham-nat-naturwissenschaftstest-erklaert/",
    },
    faqs: [
      {
        question: "Welche Unis nutzen den HAM-Nat?",
        answer:
          "In der Humanmedizin unter anderem Hamburg, Magdeburg und Greifswald; für Zahn- und Pharmazie an einzelnen dieser Standorte. Prüfe vor der Vorbereitung, ob dein Wunschort ihn überhaupt einsetzt.",
      },
      {
        question: "Was ist der Unterschied zum TMS?",
        answer:
          "Der HAM-Nat prüft gelerntes naturwissenschaftliches Wissen, der TMS kognitive Fähigkeiten ohne Fachwissen. Der TMS ist breiter anerkannt, der HAM-Nat nur an wenigen Standorten relevant.",
      },
      {
        question: "Wie bereite ich mich auf den HAM-Nat vor?",
        answer:
          "Wie auf eine Klausur: entlang des Themenkatalogs in Biologie, Chemie, Physik und Mathematik lernen und mit Übungsaufgaben trainieren. Plane mehrere Monate Vorlauf bis zum Testtermin ein.",
      },
    ],
  },
  {
    slug: "studierfaehigkeitstest",
    term: "Studierfähigkeitstest",
    category: "Tests",
    short: "Oberbegriff für Eignungstests wie TMS, HAM-Nat oder MedAT.",
    definition:
      "Studierfähigkeitstest ist der Oberbegriff für Eignungstests, die die Zulassung zum Medizinstudium unterstützen. Dazu zählen der TMS, der HAM-Nat und im Ausland etwa der MedAT oder der EMS. Sie prüfen je nach Test kognitive Fähigkeiten oder gelerntes Fachwissen.",
    seoTitle: "Studierfähigkeitstest: TMS, HAM-Nat & Co. | ncrechner",
    metaDescription:
      "Studierfähigkeitstest erklärt: Welche Tests dazugehören (TMS, HAM-Nat, MedAT, EMS), was sie unterscheidet, in welchen Quoten sie wirken und welcher Test sich.",
    heroImage: "/images/blog/t-vergleich.jpg",
    heroImageAlt: "Vergleich verschiedener Studierfähigkeitstests fürs Medizinstudium",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was ein Studierfähigkeitstest ist",
        paragraphs: [
          "Studierfähigkeitstests sind standardisierte Tests, die die Eignung fürs Medizinstudium einschätzen. Sie ergänzen die Abiturnote und geben Hochschulen ein zusätzliches, notenunabhängiges Kriterium.",
          "Je nach Test steht die kognitive Leistungsfähigkeit oder gelerntes Fachwissen im Vordergrund.",
        ],
      },
      {
        id: "welche",
        heading: "Welche Tests dazugehören",
        paragraphs: [
          "Die wichtigsten im deutschsprachigen Raum sind der TMS (breit anerkannt in Deutschland), der HAM-Nat (an einzelnen Standorten), der MedAT (Österreich) und der EMS (Schweiz). Für internationale Bewerber kommt der TestAS hinzu.",
          "Sie unterscheiden sich in Inhalt, Termin und Bewertung deutlich.",
        ],
      },
      {
        id: "quoten",
        heading: "Wo die Tests wirken",
        paragraphs: [
          "In Deutschland zählen Studierfähigkeitstests vor allem im Auswahlverfahren der Hochschulen und in der Zusätzlichen Eignungsquote. In der Abiturbestenquote spielen sie keine Rolle.",
          "Im Ausland sind sie oft das zentrale oder alleinige Auswahlinstrument.",
        ],
      },
      {
        id: "welcher",
        heading: "Welcher Test sich lohnt",
        paragraphs: [
          "Das hängt von deinen Ziel-Hochschulen ab. Der TMS öffnet die meisten deutschen Standorte, spezielle Tests kommen gezielt hinzu. Gemeinsam ist allen: Sie sind trainierbar.",
          "Mit der ncrechner App siehst du, welcher Test an deinen Wunsch-Unis gefragt ist und wie stark er dort zählt.",
        ],
      },
    ],
    related: ["tms", "ham-nat", "medat"],
    faqs: [
      {
        question: "Welcher Studierfähigkeitstest ist der wichtigste?",
        answer:
          "In Deutschland der TMS, weil ihn die meisten Hochschulen anerkennen. Spezielle Tests wie der HAM-Nat lohnen gezielt für einzelne Standorte, der MedAT und der EMS gelten für Österreich und die Schweiz.",
      },
      {
        question: "In welchen Quoten zählen die Tests?",
        answer:
          "In Deutschland im Auswahlverfahren der Hochschulen (AdH) und in der Zusätzlichen Eignungsquote (ZEQ). In der Abiturbestenquote zählt allein die Note.",
      },
      {
        question: "Kann ich mehrere Tests kombinieren?",
        answer:
          "Ja. Viele legen den TMS ab und ergänzen ihn je nach Wunschort um HAM-Nat, MedAT oder EMS. Achte auf die Termine, damit sich die Vorbereitung nicht überschneidet.",
      },
    ],
  },
  {
    slug: "medat",
    term: "MedAT",
    category: "Tests",
    short: "Aufnahmetest der öffentlichen Medizin-Unis in Österreich.",
    definition:
      "Der MedAT ist der Aufnahmetest, über den Österreichs öffentliche Medizin-Universitäten ihre Plätze vergeben – ganz ohne Abiturnote. Er kombiniert naturwissenschaftliches Wissen, kognitive Aufgaben und sozial-emotionale Kompetenzen und findet nur einmal im Jahr statt.",
    seoTitle: "MedAT: Aufnahmetest fürs Studium in Österreich | ncrechner",
    metaDescription:
      "MedAT erklärt: der österreichische Aufnahmetest fürs Medizinstudium, was er prüft, wie die EU-Quote deutsche Bewerber betrifft, der Jahrestermin.",
    heroImage: "/images/blog/theme-world-travel.jpg",
    heroImageAlt: "Studium im Ausland über den MedAT in Österreich",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was der MedAT ist",
        paragraphs: [
          "Die öffentlichen Medizin-Universitäten in Wien, Graz, Innsbruck und Linz wählen fast ausschließlich über den MedAT aus. Die Abiturnote spielt keine Rolle, gezählt wird allein das Testergebnis.",
          "Damit ist der MedAT ein eigener Weg neben dem deutschen NC-System.",
        ],
      },
      {
        id: "inhalt",
        heading: "Aufbau des Tests",
        paragraphs: [
          "Der MedAT prüft eine Mischung aus naturwissenschaftlichem Grundwissen (Biologie, Chemie, Physik, Mathematik), kognitiven Fähigkeiten ähnlich dem TMS und sozial-emotionalen Kompetenzen.",
          "Er ist damit breiter aufgestellt als der reine TMS und verlangt sowohl Stofflernen als auch Fähigkeitstraining.",
        ],
      },
      {
        id: "eu-quote",
        heading: "EU-Quote und Jahrestermin",
        paragraphs: [
          "Ein Großteil der Plätze ist für Bewerber mit österreichischem Schulabschluss reserviert; deutsche Bewerber konkurrieren um das EU-Kontingent. Studiengebühren fallen für EU-Bürger praktisch keine an.",
          "Der Haken: Der MedAT findet nur einmal jährlich statt, mit früher Anmeldefrist. Wer den Termin verpasst, verliert ein ganzes Jahr.",
        ],
      },
      {
        id: "vs-tms",
        heading: "MedAT oder TMS?",
        paragraphs: [
          "Für die deutsche Bewerbung zählt der TMS, für Österreich der MedAT. Beide lassen sich kombinieren, weil sich Teile des kognitiven Trainings überschneiden.",
          "Sinnvoll ist der MedAT als zusätzliche Schiene neben der deutschen Bewerbung – nicht als alleiniger Plan.",
        ],
      },
    ],
    related: ["studierfaehigkeitstest", "tms", "numerus-clausus"],
    article: {
      label: "MedAT: Studium in Österreich",
      href: "/ratgeber/zulassung/medat-medizinstudium-oesterreich/",
    },
    faqs: [
      {
        question: "Zählt mein Abitur beim MedAT?",
        answer:
          "Nein. Die österreichischen öffentlichen Medizin-Unis wählen allein über das MedAT-Ergebnis aus. Ein durchschnittliches Abitur ist kein Hindernis, ein starker Testtag aber Pflicht.",
      },
      {
        question: "Wie oft kann ich den MedAT schreiben?",
        answer:
          "Einmal pro Jahr, da es nur einen Termin gibt. Eine Wiederholung ist in Folgejahren möglich. Wegen des einmaligen Termins ist eine gründliche Vorbereitung besonders wichtig.",
      },
      {
        question: "Kann ich MedAT und die deutsche Bewerbung kombinieren?",
        answer:
          "Ja, und das ist oft die klügste Strategie. Beide Verfahren laufen unabhängig, der MedAT ist eine zusätzliche Chance neben dem deutschen Vergabeverfahren.",
      },
    ],
  },
  {
    slug: "landarztquote",
    term: "Landarztquote",
    category: "Quoten",
    short: "Studienplatz gegen spätere Tätigkeit in unterversorgten Regionen.",
    definition:
      "Die Landarztquote reserviert einen Teil der Medizin-Studienplätze für Bewerber, die sich verpflichten, nach dem Studium und der Facharztweiterbildung rund zehn Jahre als Hausärztin oder Hausarzt in unterversorgten ländlichen Regionen zu arbeiten. Die Auswahl läuft über Eignung und Motivation, nicht über den NC.",
    seoTitle: "Landarztquote: Studienplatz ohne NC erklärt | ncrechner",
    metaDescription:
      "Landarztquote erklärt: Anteil der Plätze je Bundesland, die rund zehnjährige Verpflichtung, die Auswahl über Test und Interview, Vertragsstrafen und frühe.",
    heroImage: "/images/blog/z-landarztquote.jpg",
    heroImageAlt: "Hausärztliche Versorgung auf dem Land als Ziel der Landarztquote",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was die Landarztquote ist",
        paragraphs: [
          "Über die Landarztquote werden Plätze weitgehend unabhängig vom Abischnitt vergeben. Je nach Bundesland macht sie einen einstelligen Prozentanteil der Plätze aus.",
          "Die Quote wird von den Ländern organisiert und ist inzwischen in den meisten Bundesländern eingeführt.",
        ],
      },
      {
        id: "verpflichtung",
        heading: "Die Verpflichtung",
        paragraphs: [
          "Im Gegenzug für den Platz verpflichtest du dich, nach Studium und Weiterbildung für einen festgelegten Zeitraum, oft rund zehn Jahre, in der hausärztlichen Versorgung einer unterversorgten Region zu arbeiten.",
          "Die genaue Region richtet sich nach dem Bedarf des Landes, der Spielraum bei der Ortswahl ist begrenzt.",
        ],
      },
      {
        id: "auswahl",
        heading: "Auswahl und Fristen",
        paragraphs: [
          "Ausgewählt wird über ein Punktesystem aus Eignung und Motivation: ein Studierfähigkeitstest, einschlägige Ausbildung oder Berufserfahrung sowie ein Auswahlgespräch spielen eine Rolle. Die Note ist nicht ausschlaggebend.",
          "Die Bewerbung läuft getrennt über die Länder und hat oft sehr frühe, eigene Fristen, meist im Frühjahr.",
        ],
      },
      {
        id: "bindung",
        heading: "Bindung und Vertragsstrafen",
        paragraphs: [
          "Die lange Bindung ist der Preis: Wer die Verpflichtung nicht erfüllt, muss mit erheblichen Vertragsstrafen rechnen. Ausnahmen gelten etwa bei schwerer Krankheit.",
          "Die Landarztquote ist deshalb ein echter Weg für alle, die sich eine hausärztliche Tätigkeit auf dem Land wirklich vorstellen können – nicht als Notlösung gedacht.",
        ],
      },
    ],
    related: ["zusaetzliche-eignungsquote", "haertefallquote", "numerus-clausus"],
    article: {
      label: "Landarztquote erklärt",
      href: "/ratgeber/zulassung/landarztquote-medizin-erklaert/",
    },
    faqs: [
      {
        question: "Wie lange ist die Verpflichtung bei der Landarztquote?",
        answer:
          "In der Regel rund zehn Jahre hausärztliche Tätigkeit in einer unterversorgten Region, nach Studium und Facharztweiterbildung. Bei Nichterfüllung drohen hohe Vertragsstrafen.",
      },
      {
        question: "Zählt mein Abitur für die Landarztquote?",
        answer:
          "Kaum. Ausgewählt wird über ein Punktesystem aus Test, einschlägiger Erfahrung und Auswahlgespräch. Damit ist die Quote auch mit durchschnittlichem Abitur ein realistischer Weg.",
      },
      {
        question: "Wann muss ich mich für die Landarztquote bewerben?",
        answer:
          "Getrennt vom zentralen Verfahren über das jeweilige Bundesland, oft mit sehr frühen Fristen im Frühjahr. Informiere dich rechtzeitig, da die Termine je Land unterschiedlich sind.",
      },
    ],
  },
  {
    slug: "haertefallquote",
    term: "Härtefallquote",
    category: "Quoten",
    short: "Sehr kleine Quote für außergewöhnliche Ausnahmesituationen.",
    definition:
      "Die Härtefallquote vergibt nur einen sehr kleinen Anteil der Studienplätze an Bewerber, für die eine Ablehnung eine unzumutbare Härte wäre. Sie ist ein Ventil für echte Ausnahmefälle mit schwerwiegenden, in der Person liegenden Gründen, keine Abkürzung um den NC.",
    seoTitle: "Härtefallquote & Härtefallantrag Medizin | ncrechner",
    metaDescription:
      "Härtefallquote im Medizinstudium: Welcher Anteil der Plätze, welche Gründe anerkannt werden, welche Nachweise und Fristen gelten und warum sie nur.",
    heroImage: "/images/blog/theme-helping-hands.jpg",
    heroImageAlt: "Unterstützung in einer besonderen Lebenssituation – Symbol für die Härtefallquote",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was die Härtefallquote ist",
        paragraphs: [
          "Über die Härtefallquote wird nur ein sehr kleiner Anteil der Plätze vergeben – meist im niedrigen einstelligen Prozentbereich. Sie ist für Menschen gedacht, denen ein weiteres Wartejahr nachweislich nicht zuzumuten ist.",
          "Der Maßstab ist streng: Es geht nicht um allgemeine Nachteile, sondern um eine besondere, belegbare Ausnahmesituation.",
        ],
      },
      {
        id: "gruende",
        heading: "Welche Gründe anerkannt werden",
        paragraphs: [
          "Anerkannt werden vor allem gesundheitliche und besondere familiäre oder soziale Umstände, die sich durch Warten verschlechtern würden.",
        ],
        list: [
          "Schwere, fortschreitende Erkrankungen mit fachärztlichem Gutachten",
          "Schwerbehinderung mit Auswirkung auf das Studium",
          "Pflegeverpflichtungen für nahe Angehörige",
          "Belegbare außergewöhnliche familiäre oder soziale Notlagen",
        ],
      },
      {
        id: "nachweise",
        heading: "Nachweise und Fristen",
        paragraphs: [
          "Ein Härtefallantrag steht und fällt mit aktuellen, aussagekräftigen Gutachten und Bescheinigungen. Bloße Schilderungen ohne Belege werden in der Regel nicht anerkannt.",
          "Der Antrag muss fristgerecht und meist zusätzlich in Papierform bei hochschulstart.de eingereicht werden – üblich sind Stichtage um den 15. Januar beziehungsweise 15. Juli.",
        ],
      },
      {
        id: "einordnung",
        heading: "Nur eine Ergänzung",
        paragraphs: [
          "Wegen der winzigen Quote sollte der Härtefallantrag nie der alleinige Plan sein. Bewirb dich parallel regulär über die anderen Quoten.",
          "Prüfe zusätzlich, ob ein Nachteilsausgleich der bessere Weg ist, wenn deine Abiturnote durch die Umstände beeinträchtigt wurde.",
        ],
      },
    ],
    related: ["nachteilsausgleich", "landarztquote", "numerus-clausus"],
    article: {
      label: "Nachteilsausgleich & Härtefallantrag Medizin",
      href: "/ratgeber/zulassungsrechner/nachteilsausgleich-medizin-haertefallantrag/",
    },
    faqs: [
      {
        question: "Wie viele Plätze gehen an Härtefälle?",
        answer:
          "Nur ein sehr kleiner Anteil je Studiengang, meist im niedrigen einstelligen Prozentbereich. Die Quote ist bewusst klein, weil sie ausschließlich außergewöhnliche Ausnahmesituationen abfedern soll.",
      },
      {
        question: "Zählt ein durchschnittliches Abitur als Härtefall?",
        answer:
          "Nein. Eine mittlere Note oder ein nicht bestandener Test sind keine anerkannten Gründe. Es zählen nur schwerwiegende, in der Person liegende Umstände, die ein Warten unzumutbar machen.",
      },
      {
        question: "Welche Nachweise brauche ich?",
        answer:
          "Aktuelle fachärztliche Gutachten, amtliche Bescheinigungen oder Belege über Pflege- und Notlagen. Der Antrag muss fristgerecht und häufig zusätzlich in Papierform bei hochschulstart.de eingehen.",
      },
    ],
  },
  {
    slug: "wartesemester",
    term: "Wartesemester",
    category: "Grundbegriffe",
    short: "Frühere Warte-Quote – heute kein eigener Zulassungsweg mehr.",
    definition:
      "Wartesemester sind die Halbjahre zwischen Abitur und Studienbeginn. Früher gab es dafür eine eigene Quote, die seit der Reform 2020 abgeschafft ist. Reines Warten führt heute nicht mehr zum Studienplatz – entscheidend ist, was du in der Zwischenzeit sinnvoll aufbaust.",
    seoTitle: "Wartesemester Medizin: Was heute noch zählt | ncrechner",
    metaDescription:
      "Wartesemester erklärt: Warum die alte Warte-Quote seit 2020 abgeschafft ist, was reines Warten heute bringt und wie du die Zeit stattdessen für deine.",
    heroImage: "/images/blog/z-wartesemester.jpg",
    heroImageAlt: "Sanduhr als Symbol für Wartesemester im Medizin-Zulassungsverfahren",
    body: [],
    sections: [
      {
        id: "was-sind",
        heading: "Was Wartesemester sind",
        paragraphs: [
          "Als Wartesemester zählten früher die Halbjahre nach dem Abitur, in denen man nicht immatrikuliert war. Je mehr Wartesemester, desto besser die Chancen in einer eigenen Warte-Quote.",
          "Diese Logik ist Geschichte: Der Begriff hält sich hartnäckig, führt heute aber in die Irre.",
        ],
      },
      {
        id: "reform",
        heading: "Die Abschaffung der Warte-Quote",
        paragraphs: [
          "Mit der Reform 2020 wurde die reine Wartezeitquote abgeschafft. Es gibt keinen Automatismus mehr, der bloßes Warten mit einem Studienplatz belohnt.",
          "Wer heute nur wartet, verbessert seine Zulassungschancen dadurch nicht.",
        ],
      },
      {
        id: "sinnvoll-nutzen",
        heading: "Die Zeit sinnvoll nutzen",
        paragraphs: [
          "Was heute zählt, ist der Aufbau echter Bausteine: ein guter TMS, ein Freiwilligendienst oder eine abgeschlossene Ausbildung wirken in AdH und ZEQ.",
          "So wird aus früherer Wartezeit ein aktiv gestärktes Profil, das die Bewerbung real verbessert.",
        ],
      },
      {
        id: "strategie",
        heading: "Was das für deine Planung heißt",
        paragraphs: [
          "Plane die Zeit nach dem Abitur gezielt: Welcher Baustein bringt an deinen Wunsch-Hochschulen die meisten Punkte? Oft ist es die Kombination aus TMS und Dienst oder Ausbildung.",
          "Die ncrechner App zeigt dir, welcher Schritt deine Chancen am stärksten hebt.",
        ],
      },
    ],
    related: ["zusaetzliche-eignungsquote", "freiwilligendienst", "berufsausbildung"],
    article: {
      label: "Wartesemester heute",
      href: "/ratgeber/zulassung/wartesemester-medizin-heute/",
    },
    faqs: [
      {
        question: "Bringen Wartesemester noch einen Studienplatz?",
        answer:
          "Nein. Die reine Wartezeitquote wurde 2020 abgeschafft. Bloßes Warten verbessert die Zulassungschancen heute nicht mehr, entscheidend ist, was du in der Zeit aufbaust.",
      },
      {
        question: "Was zählt heute statt Wartesemestern?",
        answer:
          "Ein guter TMS, ein Freiwilligendienst oder eine abgeschlossene Ausbildung. Diese Bausteine wirken im Auswahlverfahren der Hochschulen und in der Zusätzlichen Eignungsquote.",
      },
      {
        question: "Warum hört man trotzdem oft von Wartesemestern?",
        answer:
          "Der Begriff stammt aus der Zeit vor der Reform und hält sich hartnäckig. Heute führt er in die Irre, weil es keinen Automatismus mehr gibt, der Wartezeit belohnt.",
      },
    ],
  },
  {
    slug: "losverfahren",
    term: "Losverfahren",
    category: "Grundbegriffe",
    short: "Vergabe übrig gebliebener Restplätze per Zufall.",
    definition:
      "Im Losverfahren vergeben Hochschulen Restplätze, die nach dem regulären Verfahren frei geblieben sind – per Zufall und unabhängig von Note oder Test. Die Chancen sind sehr gering, doch der Aufwand ist klein: Für viele ist es die letzte Möglichkeit im selben Bewerbungsjahr.",
    seoTitle: "Losverfahren Medizin: Restplätze per Los | ncrechner",
    metaDescription:
      "Losverfahren erklärt: Wie Restplätze per Zufall vergeben werden, wie und wo du dich bewirbst, welche Fristen gelten, wie realistisch die Chancen sind und was.",
    heroImage: "/images/blog/z-losverfahren.jpg",
    heroImageAlt: "Zufallsvergabe von Restplätzen im Losverfahren",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was das Losverfahren ist",
        paragraphs: [
          "Bleiben nach dem regulären Verfahren Plätze frei, werden sie unter den Bewerbern verlost. Note und Testergebnis spielen dabei keine Rolle – es entscheidet allein der Zufall.",
          "Das Losverfahren ist damit die letzte Stufe der Platzvergabe eines Bewerbungsjahres.",
        ],
      },
      {
        id: "bewerbung",
        heading: "Bewerbung und Fristen",
        paragraphs: [
          "Man bewirbt sich meist separat und direkt bei den einzelnen Hochschulen, oft formlos über ein Online-Formular. Eine vorherige Teilnahme am regulären Verfahren ist in der Regel nicht nötig.",
          "Die Fristen sind kurz und liegen kurz vor Semesterbeginn. Ergebnisse werden häufig nur online gestellt, ohne gesonderten Bescheid.",
        ],
      },
      {
        id: "chancen",
        heading: "Wie realistisch die Chancen sind",
        paragraphs: [
          "Die Erfolgsquote ist niedrig, weil auf wenige Restplätze sehr viele Bewerber kommen. Wer sich an mehreren Hochschulen parallel bewirbt, erhöht die ohnehin kleinen Chancen etwas.",
          "Als alleinige Strategie taugt das Losverfahren nicht, als ergänzende Option aber durchaus.",
        ],
      },
      {
        id: "tipps",
        heading: "Worauf du achten solltest",
        paragraphs: [
          "Behalte die Portale der Hochschulen aktiv im Blick und halte deine Unterlagen bereit, damit du die kurzen Fristen nicht verpasst. Internationale Bewerber brauchen oft zusätzliche Nachweise mit Vorlauf.",
          "Plane parallel die verlässlicheren Wege: reguläres Verfahren, TMS und gezielte Ortswahl mit der ncrechner App.",
        ],
      },
    ],
    related: ["nachruecken", "koordinierungsphase", "numerus-clausus"],
    article: {
      label: "Losverfahren & Restplätze",
      href: "/ratgeber/zulassung/losverfahren-medizin-restplaetze/",
    },
    faqs: [
      {
        question: "Brauche ich für das Losverfahren gute Noten?",
        answer:
          "Nein. Im Losverfahren entscheidet allein der Zufall. Note und Testergebnis spielen keine Rolle, weshalb es auch mit schwächerem Profil eine, wenn auch kleine, Chance bietet.",
      },
      {
        question: "Wie stehen meine Chancen im Losverfahren?",
        answer:
          "Gering, weil auf wenige Restplätze sehr viele Bewerber kommen. Eine Bewerbung an mehreren Hochschulen erhöht die Chancen leicht, ersetzt aber keine Strategie über die regulären Quoten.",
      },
      {
        question: "Wie bewerbe ich mich fürs Losverfahren?",
        answer:
          "Meist direkt und separat bei den einzelnen Hochschulen über deren Portale, oft mit sehr kurzen Fristen kurz vor Semesterbeginn. Eine Teilnahme am regulären Verfahren ist dafür nicht zwingend.",
      },
    ],
  },
  {
    slug: "hochschulzugangsberechtigung",
    term: "Hochschulzugangsberechtigung (HZB)",
    category: "Grundbegriffe",
    short: "Die formale Voraussetzung fürs Studium – meist das Abitur.",
    definition:
      "Die Hochschulzugangsberechtigung (HZB) ist der formale Nachweis, dass du in Deutschland studieren darfst – in der Regel das Abitur. Sie ist Pflicht für die Einschreibung. Auch beruflich Qualifizierte oder Menschen mit fachgebundener Hochschulreife können unter Bedingungen eine HZB fürs Medizinstudium besitzen.",
    seoTitle: "Hochschulzugangsberechtigung (HZB) erklärt | ncrechner",
    metaDescription:
      "Hochschulzugangsberechtigung erklärt: Welche Arten es gibt (allgemeine, fachgebundene, Fachhochschulreife), wie der Zugang ohne Abitur funktioniert und was.",
    heroImage: "/images/blog/medizin-zulassung-guide.jpg",
    heroImageAlt: "Abiturzeugnis als häufigste Form der Hochschulzugangsberechtigung",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was die HZB ist",
        paragraphs: [
          "Die HZB weist die schulische oder berufliche Qualifikation nach, die du zum Studium brauchst. Ohne sie ist keine Einschreibung möglich – sie ist die einzige echte Pflichtvoraussetzung.",
          "Alles Weitere, etwa TMS, Dienste oder Ausbildungen, sind Auswahlkriterien oder Nachweise, die erst im Studium verlangt werden.",
        ],
      },
      {
        id: "arten",
        heading: "Die Arten der HZB",
        paragraphs: [
          "Je nach Abschluss unterscheidet sich, was du studieren darfst.",
        ],
        list: [
          "Allgemeine Hochschulreife (Abitur): Zugang zu allen Fächern und Hochschulen",
          "Fachgebundene Hochschulreife: Zugang zu bestimmten, fachlich passenden Studiengängen",
          "Fachhochschulreife: Studium in der Regel nur an Fachhochschulen",
        ],
      },
      {
        id: "ohne-abitur",
        heading: "Zugang ohne klassisches Abitur",
        paragraphs: [
          "Beruflich Qualifizierte können in vielen Bundesländern auch ohne Abitur studieren – mit abgeschlossener Ausbildung und Berufserfahrung, teils nach einer Eignungsprüfung. Ein medizinnaher Hintergrund wie Pflege wird dabei oft geschätzt.",
          "Ausländische Abschlüsse müssen meist anerkannt oder über eine Feststellungsprüfung am Studienkolleg ergänzt werden.",
        ],
      },
      {
        id: "medizin",
        heading: "Was das fürs Medizinstudium heißt",
        paragraphs: [
          "Auch mit HZB konkurrierst du am Ende um dieselben knappen Plätze. Die HZB öffnet die Tür, den Ausschlag geben dann Note, Test und weitere Kriterien in den Quoten.",
          "Ohne klassisches Abitur hängen die genauen Regeln stark vom Bundesland und der Hochschule ab – kläre sie früh und standortgenau.",
        ],
      },
    ],
    related: ["numerus-clausus", "abiturbestenquote", "berufsausbildung"],
    article: {
      label: "Medizin studieren ohne Abitur",
      href: "/ratgeber/zulassung/medizin-studieren-ohne-abitur/",
    },
    faqs: [
      {
        question: "Ist das Abitur die einzige HZB?",
        answer:
          "Nein. Neben der allgemeinen Hochschulreife gibt es die fachgebundene Hochschulreife und die Fachhochschulreife. Auch beruflich Qualifizierte können unter Bedingungen eine HZB erwerben.",
      },
      {
        question: "Kann ich mit Fachhochschulreife Medizin studieren?",
        answer:
          "In der Regel nicht direkt an einer Universität, da Medizin dort studiert wird. Es kann Sonderwege über die berufliche Qualifikation oder eine Prüfung geben – die Regeln legen die Bundesländer fest.",
      },
      {
        question: "Zählt eine ausländische HZB?",
        answer:
          "Häufig erst nach Anerkennung oder einer Feststellungsprüfung am Studienkolleg. Prüfe früh, welche Nachweise deine Ziel-Hochschule verlangt, weil die Verfahren dafür unterschiedlich sind.",
      },
    ],
  },
  {
    slug: "freiwilligendienst",
    term: "Freiwilligendienst (FSJ / BFD)",
    category: "Bewerbung",
    short: "Dienstjahr, das in AdH und ZEQ Punkte bringen kann.",
    definition:
      "Ein Freiwilligendienst wie das FSJ oder der Bundesfreiwilligendienst (BFD) ist ein meist einjähriger Dienst im sozialen oder gesundheitlichen Bereich, der an vielen Hochschulen in AdH und ZEQ mit Punkten gewertet wird. Er ist der am schnellsten erarbeitbare Baustein und zugleich ein realistischer Praxischeck.",
    seoTitle: "Freiwilligendienst FSJ & BFD fürs Medizinstudium | ncrechner",
    metaDescription:
      "Freiwilligendienst fürs Medizinstudium: Wie FSJ und BFD in AdH und ZEQ Punkte bringen, welche Mindestdauer gilt, wo du ihn machst und was er über Punkte.",
    heroImage: "/images/blog/b-fsj-ausbildung.jpg",
    heroImageAlt: "Freiwillige im Gesundheitsbereich während eines FSJ",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was ein Freiwilligendienst ist",
        paragraphs: [
          "Das Freiwillige Soziale Jahr (FSJ) richtet sich vor allem an junge Menschen unter 27, der Bundesfreiwilligendienst (BFD) steht allen Altersgruppen offen. Beide bieten praktische Erfahrung im sozialen oder gesundheitlichen Bereich, oft mit einem monatlichen Taschengeld und Sozialversicherung.",
          "Für die Medizin-Bewerbung zählt der Dienst als anerkannter Baustein in mehreren Quoten.",
        ],
      },
      {
        id: "quoten-dauer",
        heading: "Punkte, Quoten und Mindestdauer",
        paragraphs: [
          "Gewertet wird der Dienst im Auswahlverfahren der Hochschulen (AdH) und in der Zusätzlichen Eignungsquote (ZEQ), nicht in der Abiturbestenquote. Üblich ist eine zusammenhängende Mindestdauer von rund elf bis zwölf Monaten.",
          "Ein halbes Jahr reicht oft nicht für die volle Wertung. Der Dienst muss in der Regel bis zur Bewerbungsfrist abgeschlossen und nachgewiesen sein.",
        ],
      },
      {
        id: "wo",
        heading: "Wo du den Dienst machen kannst",
        paragraphs: [
          "Anerkannte Einsatzstellen gibt es in Kliniken und Universitätsmedizin, in Pflege- und Reha-Einrichtungen sowie im Rettungsdienst bei DRK, Maltesern, Johannitern oder Feuerwehren.",
          "Bewirb dich früh, idealerweise rund ein Jahr vor dem gewünschten Start, weil beliebte Stellen schnell vergeben sind.",
        ],
      },
      {
        id: "mehrwert",
        heading: "Mehr als nur Punkte",
        paragraphs: [
          "Neben den Punkten liefert ein Dienstjahr echte Erfahrung: erste praktische Fertigkeiten, mehr Sicherheit im Umgang mit Patienten und ein ehrlicher Eindruck vom Berufsalltag. Teile lassen sich manchmal aufs spätere Pflegepraktikum anrechnen.",
          "Mit der ncrechner App siehst du, an welchen Hochschulen ein Dienst wie viel bringt und wie er sich mit einem TMS kombinieren lässt.",
        ],
      },
    ],
    related: ["anerkannter-dienst", "berufsausbildung", "zusaetzliche-eignungsquote", "pflegepraktikum"],
    article: {
      label: "Freiwilligendienst fürs Medizinstudium",
      href: "/ratgeber/zulassung/freiwilligendienst-medizin-fsj-bfd/",
    },
    faqs: [
      {
        question: "Wie lange muss ein FSJ oder BFD dauern?",
        answer:
          "Für die volle Wertung meist rund elf bis zwölf Monate am Stück. Kürzere Dienste werden häufig nicht anerkannt. Die genaue Mindestdauer steht in der Zulassungsordnung der jeweiligen Hochschule.",
      },
      {
        question: "In welchen Quoten zählt der Dienst?",
        answer:
          "Im AdH und in der ZEQ. In der Abiturbestenquote zählt er nicht, weil dort ausschließlich die Abiturnote entscheidet.",
      },
      {
        question: "Was ist der Unterschied zwischen FSJ und BFD?",
        answer:
          "Das FSJ richtet sich vor allem an junge Menschen unter 27, der BFD steht allen Altersgruppen offen und ist oft flexibler. Für die Wertung in der Bewerbung werden beide anerkannt.",
      },
    ],
  },
  {
    slug: "berufsausbildung",
    term: "Anerkannte Berufsausbildung",
    category: "Bewerbung",
    short: "Abgeschlossene medizinnahe Ausbildung, die in AdH und ZEQ Punkte bringt.",
    definition:
      "Eine anerkannte Berufsausbildung ist eine abgeschlossene, staatlich geregelte Ausbildung in einem medizinnahen Beruf. Sie bringt an vielen Hochschulen Punkte im Auswahlverfahren der Hochschulen (AdH) und in der Zusätzlichen Eignungsquote (ZEQ) – oft mehr als ein Dienst, dafür kostet sie zwei bis drei Jahre.",
    seoTitle: "Anerkannte Berufsausbildung: Punkte für Medizin | ncrechner",
    metaDescription:
      "Anerkannte Berufsausbildung fürs Medizinstudium: Welche Ausbildungen zählen, in welchen Quoten sie Punkte bringen, welche Fristen gelten und wie stark sie.",
    heroImage: "/images/blog/b-ausbildung-waehlen.jpg",
    heroImageAlt:
      "Auszubildende in der Pflege – eine anerkannte Berufsausbildung stärkt die Medizin-Bewerbung",
    body: [],
    sections: [
      {
        id: "was-gilt",
        heading: "Was als anerkannte Berufsausbildung gilt",
        paragraphs: [
          "Gemeint ist eine abgeschlossene, staatlich geregelte Ausbildung in einem Beruf mit klarem Bezug zur Medizin oder Pflege. Nicht jede Ausbildung zählt – entscheidend ist die medizinnahe Ausrichtung und der formale Abschluss.",
          "Der Nachweis wirkt in den Auswahlverfahren als eigenständiges Kriterium und signalisiert zugleich echte Praxiserfahrung im Gesundheitswesen.",
        ],
      },
      {
        id: "quoten",
        heading: "In welchen Quoten die Ausbildung zählt",
        paragraphs: [
          "Punkte bringt die Ausbildung in zwei der drei Quoten: im Auswahlverfahren der Hochschulen (AdH) und besonders in der Zusätzlichen Eignungsquote (ZEQ). In der Abiturbestenquote spielt sie dagegen keine Rolle – dort zählt allein die Note.",
          "Weil die ZEQ komplett notenunabhängig ist, ist sie für viele mit Ausbildung der aussichtsreichste Weg.",
        ],
      },
      {
        id: "welche-berufe",
        heading: "Welche Ausbildungen anerkannt werden",
        paragraphs: [
          "Anerkannt wird ein breites Spektrum medizinnaher Berufe. Welche genau zählen und wie hoch, legt jede Hochschule in ihrer Zulassungsordnung fest.",
        ],
        list: [
          "Pflegeberufe: Kranken-, Kinderkranken- und Altenpflege sowie Hebamme/Entbindungspfleger",
          "Praxis- und Assistenzberufe: MFA, ZFA, OTA, ATA",
          "Technische Berufe: MTA (Labor, Radiologie), RTA, Laborberufe",
          "Rettungsdienst: Notfallsanitäter und Rettungsassistenz",
          "Therapieberufe: Physio-, Ergotherapie, Logopädie",
        ],
      },
      {
        id: "voraussetzungen",
        heading: "Voraussetzungen: abgeschlossen und fristgerecht",
        paragraphs: [
          "Grundregel: Die Ausbildung muss abgeschlossen sein. Eine noch laufende Ausbildung zählt in der Regel nicht. Einige Hochschulen verlangen zusätzlich eine Mindestdauer an einschlägiger Berufstätigkeit, häufig rund zwölf Monate.",
          "Wichtig sind die Bewerbungsfristen: Für das Sommersemester musst du den Abschluss meist bis Ende Januar, für das Wintersemester bis Ende Juli nachweisen. Wer knapp dran ist, sollte den Abschlusstermin genau mit der Frist abgleichen.",
        ],
      },
      {
        id: "aufwand-tms",
        heading: "Aufwand, Wirkung und Kombination mit dem TMS",
        paragraphs: [
          "Zwei bis drei Jahre sind eine große Investition. Dafür wird die Ausbildung an vielen Standorten höher gewertet als ein einjähriger Dienst. Am stärksten wirkt die Kombination aus abgeschlossener Ausbildung und einem guten TMS – sie gehört in der ZEQ zu den besten Profilen überhaupt.",
          "Mit der ncrechner App siehst du pro Hochschule, wie stark deine Ausbildung dort zählt und welcher zusätzliche Baustein deine Chancen am meisten hebt.",
        ],
      },
      {
        id: "berufsabschluss",
        heading: "Ein echter Berufsabschluss als Rückversicherung",
        paragraphs: [
          "Der oft übersehene Vorteil: Nach der Ausbildung hast du einen vollwertigen Berufsabschluss in der Tasche. Zieht sich der Weg zum Studienplatz, arbeitest und verdienst du im Gesundheitswesen, statt nur zu überbrücken.",
          "Gleichzeitig sammelst du genau die Praxiserfahrung, die im Studium und im späteren Beruf trägt.",
        ],
      },
    ],
    related: [
      "zusaetzliche-eignungsquote",
      "anerkannte-berufstaetigkeit",
      "freiwilligendienst",
      "pflegepraktikum",
    ],
    article: {
      label: "Berufsausbildung & Berufstätigkeit",
      href: "/ratgeber/zulassung/berufsausbildung-berufstaetigkeit-medizin/",
    },
    faqs: [
      {
        question: "Zählt eine noch laufende Ausbildung?",
        answer:
          "In der Regel nicht. Für die Wertung in AdH und ZEQ muss die Ausbildung abgeschlossen und bis zur Bewerbungsfrist nachgewiesen sein – Ende Januar fürs Sommer-, Ende Juli fürs Wintersemester.",
      },
      {
        question: "In welchen Quoten hilft eine Berufsausbildung?",
        answer:
          "Im Auswahlverfahren der Hochschulen (AdH) und in der Zusätzlichen Eignungsquote (ZEQ). In der Abiturbestenquote zählt sie nicht, weil dort ausschließlich die Abiturnote entscheidet.",
      },
      {
        question: "Welche Ausbildungen werden anerkannt?",
        answer:
          "Medizinnahe Berufe wie Pflege, MFA, ZFA, MTA, RTA, OTA, ATA, Notfallsanitäter oder Therapieberufe. Die genaue Liste und Gewichtung legt jede Hochschule in ihrer Zulassungsordnung fest.",
      },
      {
        question: "Lohnt sich die Ausbildung trotz zwei bis drei Jahren?",
        answer:
          "Wenn dein Abitur allein nicht reicht, oft ja. In der ZEQ wird die Kombination aus Ausbildung und gutem TMS besonders stark gewertet – und du erwirbst nebenbei einen echten Berufsabschluss.",
      },
      {
        question: "Brauche ich zusätzlich Berufserfahrung?",
        answer:
          "An einigen Hochschulen ja, häufig rund zwölf Monate einschlägige Tätigkeit. Prüfe die Zulassungsordnung deiner Ziel-Unis, weil die Anforderungen dazu unterschiedlich sind.",
      },
    ],
  },
  {
    slug: "pflegepraktikum",
    term: "Pflegepraktikum",
    category: "Studium",
    short: "Rund 90 Tage Pflege – Studienvoraussetzung, kein Zulassungskriterium.",
    definition:
      "Das Pflegepraktikum, auch Krankenpflegedienst, umfasst rund 90 Tage in der Krankenpflege und ist im Laufe des Medizinstudiums nachzuweisen – meist bis zum ersten Staatsexamen. Es ist keine Zulassungsvoraussetzung, kann vorab absolviert aber in einzelnen Verfahren als Pluspunkt zählen.",
    seoTitle: "Pflegepraktikum im Medizinstudium: 90 Tage | ncrechner",
    metaDescription:
      "Pflegepraktikum erklärt: rund 90 Tage Krankenpflegedienst, bis wann er nachzuweisen ist, wo du ihn machst, was angerechnet wird und ob er die Bewerbung stärkt.",
    heroImage: "/images/blog/b-pflegepraktikum.jpg",
    heroImageAlt: "Pflegepraktikum in der Krankenpflege während des Medizinstudiums",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was das Pflegepraktikum ist",
        paragraphs: [
          "Das Pflegepraktikum gibt einen ersten praktischen Einblick in den Klinikalltag und die pflegerische Versorgung von Patienten. Es ist ein verpflichtender Bestandteil des Studiums, aber keine Voraussetzung für die Zulassung.",
          "Insgesamt sind rund 90 Tage abzuleisten.",
        ],
      },
      {
        id: "dauer",
        heading: "Dauer, Aufteilung und Frist",
        paragraphs: [
          "Der Dienst lässt sich am Stück oder in Abschnitten leisten; üblich ist, dass ein Teil zusammenhängend abgeleistet wird. Nachzuweisen ist er in der Regel bis zum ersten Staatsexamen.",
          "Viele nutzen die Zeit direkt nach dem Abitur, um ihn früh zu erledigen und gleichzeitig Praxisluft zu schnuppern.",
        ],
      },
      {
        id: "wo-anrechnung",
        heading: "Wo und was angerechnet wird",
        paragraphs: [
          "Absolvieren kannst du ihn in Krankenhäusern oder Reha-Einrichtungen mit pflegerischen Aufgaben. Bestimmte Vorerfahrungen werden angerechnet: ein FSJ oder BFD, ein Sanitätsdienst oder eine abgeschlossene Pflege- bzw. Rettungsausbildung.",
          "Umfang und Anrechnung unterscheiden sich je Hochschule, deshalb lohnt der frühe Blick in die Ordnung deiner Fakultät.",
        ],
      },
      {
        id: "bewerbung",
        heading: "Rolle in der Bewerbung",
        paragraphs: [
          "Für die Zulassung ist das Pflegepraktikum nicht verpflichtend. Vorab absolviert kann es aber in einzelnen Auswahlverfahren als Pluspunkt zählen oder mit einem anerkannten Dienst zusammenfallen.",
          "In erster Linie ist es jedoch ein Praxischeck und eine Pflicht fürs Studium, kein Zulassungshebel.",
        ],
      },
    ],
    related: ["freiwilligendienst", "famulatur", "berufsausbildung"],
    article: {
      label: "Pflegepraktikum planen",
      href: "/ratgeber/bewerbung/pflegepraktikum-krankenpflegedienst-planen/",
    },
    faqs: [
      {
        question: "Ist das Pflegepraktikum eine Zulassungsvoraussetzung?",
        answer:
          "Nein. Es ist eine Studienvoraussetzung, meist bis zum ersten Staatsexamen nachzuweisen. Vorab absolviert kann es in einzelnen Auswahlverfahren aber als Pluspunkt zählen.",
      },
      {
        question: "Wie lange dauert der Krankenpflegedienst?",
        answer:
          "In der Summe rund 90 Tage. Er lässt sich am Stück oder in Abschnitten ableisten, wobei ein Teil üblicherweise zusammenhängend geleistet werden sollte.",
      },
      {
        question: "Wird ein FSJ auf das Pflegepraktikum angerechnet?",
        answer:
          "Häufig ja. Ein FSJ oder BFD, ein Sanitätsdienst oder eine abgeschlossene Pflegeausbildung können ganz oder teilweise angerechnet werden. Die genauen Regeln legt jede Hochschule fest.",
      },
    ],
  },
  {
    slug: "famulatur",
    term: "Famulatur",
    category: "Studium",
    short: "Vier Monate Pflichtpraktika im klinischen Studienabschnitt.",
    definition:
      "Die Famulatur bezeichnet ärztlich begleitete Pflichtpraktika, die Studierende im klinischen Abschnitt des Medizinstudiums absolvieren. Insgesamt sind rund vier Monate zu leisten. Sie geben den ersten echten Einblick in den Klinik- und Praxisalltag und sind Voraussetzung für die weitere Ausbildung.",
    seoTitle: "Famulatur im Medizinstudium erklärt | ncrechner",
    metaDescription:
      "Famulatur erklärt: rund vier Monate Pflichtpraktika im klinischen Abschnitt, wann sie stattfinden, wo du sie ableisten kannst und welchen Zweck sie erfüllen.",
    heroImage: "/images/blog/b-pflegepraktikum.jpg",
    heroImageAlt: "Studierende während einer Famulatur in der Klinik",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was die Famulatur ist",
        paragraphs: [
          "Die Famulatur ist ein verpflichtender Bestandteil der ärztlichen Ausbildung. Studierende wenden ihr theoretisches Wissen erstmals direkt in der Patientenversorgung an, unter ärztlicher Anleitung.",
          "Sie ist damit ein zentraler Schritt vom Hörsaal in den klinischen Alltag.",
        ],
      },
      {
        id: "dauer",
        heading: "Dauer und Zeitpunkt",
        paragraphs: [
          "Insgesamt sind rund vier Monate abzuleisten, meist in mehreren Abschnitten. Die Famulatur liegt zwischen dem ersten Staatsexamen (Physikum) und dem zweiten Staatsexamen.",
          "Aufgeteilt wird sie üblicherweise auf verschiedene Versorgungsbereiche, etwa Klinik und ambulante Praxis.",
        ],
      },
      {
        id: "wo",
        heading: "Wo du famulieren kannst",
        paragraphs: [
          "Möglich sind Krankenhäuser und Reha-Einrichtungen, allgemein- und fachärztliche Praxen sowie ambulante Versorgungszentren. Auch Auslandsaufenthalte sind unter bestimmten Bedingungen anrechenbar.",
          "Voraussetzungen sind ein bestandenes Physikum, gültiger Versicherungsschutz und ein aktueller Impfnachweis; bewirb dich einige Monate im Voraus.",
        ],
      },
      {
        id: "zweck",
        heading: "Wozu die Famulatur dient",
        paragraphs: [
          "In der Famulatur übst du praktische Grundfertigkeiten: Anamnese, körperliche Untersuchung, Blutentnahmen und die Assistenz bei der Diagnostik. Genau diese Routine trägt später im Beruf.",
          "Zugleich lernst du früh unterschiedliche Fachrichtungen kennen und findest so leichter deine spätere Richtung.",
        ],
      },
    ],
    related: ["pflegepraktikum", "praktisches-jahr", "physikum"],
    faqs: [
      {
        question: "Wie lange dauert die Famulatur insgesamt?",
        answer:
          "In der Summe rund vier Monate, meist in mehreren Abschnitten über verschiedene Versorgungsbereiche verteilt. Sie liegt zwischen dem ersten und dem zweiten Staatsexamen.",
      },
      {
        question: "Wann darf ich mit der Famulatur beginnen?",
        answer:
          "Nach bestandenem Physikum. Zusätzlich brauchst du gültigen Versicherungsschutz und einen aktuellen Impfnachweis. Bewirb dich am besten einige Monate vor dem geplanten Abschnitt.",
      },
      {
        question: "Kann ich im Ausland famulieren?",
        answer:
          "Ja, unter bestimmten Bedingungen sind auch Auslandsfamulaturen anrechenbar. Kläre vorab mit deiner Fakultät, welche Nachweise und Voraussetzungen für die Anerkennung gelten.",
      },
    ],
  },
  {
    slug: "physikum",
    term: "Physikum (Erstes Staatsexamen)",
    category: "Studium",
    short: "Große Prüfung am Ende der Vorklinik, vor dem klinischen Abschnitt.",
    definition:
      "Das Physikum, offiziell Erster Abschnitt der Ärztlichen Prüfung, ist die große Prüfung am Ende der vorklinischen Studienphase nach rund vier Semestern. Es prüft die naturwissenschaftlichen und theoretischen Grundlagen und ist die Voraussetzung für den Eintritt in den klinischen Abschnitt.",
    seoTitle: "Physikum (1. Staatsexamen) erklärt | ncrechner",
    metaDescription:
      "Physikum erklärt: die große Prüfung am Ende der Vorklinik, wann sie stattfindet, wie schriftlicher und mündlicher Teil aufgebaut sind, welche Fächer geprüft.",
    heroImage: "/images/blog/theme-study-books.jpg",
    heroImageAlt: "Lernunterlagen zur Vorbereitung auf das Physikum",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was das Physikum ist",
        paragraphs: [
          "Das Physikum schließt die Vorklinik ab und entscheidet über den Übergang in den klinischen Studienabschnitt. Es gilt als eine der fordernderen Hürden, weil viel Grundlagenstoff gebündelt abgefragt wird.",
          "In Modellstudiengängen gibt es an seiner Stelle oft eine gleichwertige Prüfung in anderer Form.",
        ],
      },
      {
        id: "zeitpunkt",
        heading: "Wann es stattfindet",
        paragraphs: [
          "Das Physikum liegt nach etwa vier Semestern am Ende der vorklinischen Phase. Erst nach bestandener Prüfung beginnt der klinische Abschnitt mit Famulaturen und Kursen am Patienten.",
          "Es markiert damit den Wechsel von den Grundlagen zur angewandten Medizin.",
        ],
      },
      {
        id: "aufbau",
        heading: "Aufbau und Fächer",
        paragraphs: [
          "Das Physikum besteht aus einem schriftlichen Teil mit vielen Multiple-Choice-Fragen über zwei Tage und einem mündlich-praktischen Teil in kleinen Gruppen.",
          "Geprüft werden vor allem Anatomie, Physiologie und Biochemie, ergänzt um Physik, Chemie, Biologie sowie medizinische Psychologie und Soziologie.",
        ],
      },
      {
        id: "bestehen",
        heading: "Bestehen und Vorbereitung",
        paragraphs: [
          "Zum Bestehen ist im schriftlichen Teil ein festgelegter Anteil richtiger Antworten nötig, im mündlichen Teil mindestens eine ausreichende Bewertung. Wiederholungen sind begrenzt möglich.",
          "Bewährt sind ein strukturierter Lernplan über mehrere Wochen, das Üben mit Altfragen und Lerngruppen.",
        ],
      },
    ],
    related: ["vorklinik", "klinik", "famulatur"],
    faqs: [
      {
        question: "Wann findet das Physikum statt?",
        answer:
          "Nach rund vier Semestern am Ende der Vorklinik. Erst nach bestandenem Physikum beginnt der klinische Studienabschnitt mit Famulaturen und Kursen am Patienten.",
      },
      {
        question: "Wie ist das Physikum aufgebaut?",
        answer:
          "Aus einem schriftlichen Teil mit vielen Multiple-Choice-Fragen über zwei Tage und einem mündlich-praktischen Teil in kleinen Gruppen. Geprüft werden vor allem Anatomie, Physiologie und Biochemie.",
      },
      {
        question: "Gibt es das Physikum auch im Modellstudiengang?",
        answer:
          "Nicht in der klassischen Form. Modellstudiengänge setzen an seiner Stelle meist eine gleichwertige Prüfung ein, die in den integrierten Aufbau eingebettet ist.",
      },
    ],
  },
  {
    slug: "vorklinik",
    term: "Vorklinik",
    category: "Studium",
    short: "Erster Studienabschnitt mit den naturwissenschaftlichen Grundlagen.",
    definition:
      "Die Vorklinik ist der erste Abschnitt des Medizinstudiums. Hier werden die naturwissenschaftlichen und theoretischen Grundlagen gelegt, etwa Anatomie, Biochemie und Physiologie. Sie dauert in der Regel rund zwei Jahre und endet mit dem Physikum.",
    seoTitle: "Vorklinik im Medizinstudium erklärt | ncrechner",
    metaDescription:
      "Vorklinik erklärt: der erste Studienabschnitt mit den naturwissenschaftlichen Grundlagen, Dauer, Inhalte, Abschluss durch das Physikum und der Unterschied.",
    heroImage: "/images/blog/theme-pencil-notes.jpg",
    heroImageAlt: "Naturwissenschaftliche Grundlagen im vorklinischen Studienabschnitt",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was die Vorklinik ist",
        paragraphs: [
          "Die Vorklinik bildet das Fundament des Medizinstudiums. Sie vermittelt das naturwissenschaftliche und theoretische Grundlagenwissen, auf dem der spätere klinische Abschnitt aufbaut.",
          "Der Patientenkontakt ist in dieser Phase noch begrenzt, der Fokus liegt auf dem Verstehen von Strukturen und Prozessen des Körpers.",
        ],
      },
      {
        id: "inhalte",
        heading: "Inhalte und Dauer",
        paragraphs: [
          "Im Zentrum stehen Anatomie, Physiologie und Biochemie, ergänzt um Physik, Chemie und Biologie sowie medizinische Psychologie und Soziologie. Dazu kommen Präparierkurs und Praktika.",
          "Die Vorklinik dauert in der Regel rund zwei Jahre, also etwa vier Semester.",
        ],
      },
      {
        id: "abschluss",
        heading: "Abschluss durch das Physikum",
        paragraphs: [
          "Am Ende steht das Physikum, der erste Abschnitt der Ärztlichen Prüfung. Erst nach bestandenem Physikum beginnt der klinische Abschnitt.",
          "Das gebündelte Grundlagenwissen macht diese Prüfung anspruchsvoll.",
        ],
      },
      {
        id: "modell",
        heading: "Vorklinik im Modellstudiengang",
        paragraphs: [
          "In Modellstudiengängen ist die strikte Trennung zwischen Vorklinik und Klinik aufgehoben. Grundlagen und Praxis werden von Anfang an stärker verzahnt, ein klassisches Physikum gibt es oft nicht.",
          "Der Aufbau der ersten Studienjahre unterscheidet sich damit je nach Standort deutlich.",
        ],
      },
    ],
    related: ["physikum", "modellstudiengang", "klinik"],
    faqs: [
      {
        question: "Wie lange dauert die Vorklinik?",
        answer:
          "In der Regel rund zwei Jahre, also etwa vier Semester. Sie endet mit dem Physikum, das den Übergang in den klinischen Studienabschnitt markiert.",
      },
      {
        question: "Welche Fächer umfasst die Vorklinik?",
        answer:
          "Vor allem Anatomie, Physiologie und Biochemie, ergänzt um Physik, Chemie, Biologie sowie medizinische Psychologie und Soziologie, dazu Präparierkurs und Praktika.",
      },
      {
        question: "Gibt es die Vorklinik überall?",
        answer:
          "Im Regelstudiengang ja, klar getrennt von der Klinik. In Modellstudiengängen ist diese Trennung aufgehoben und Theorie und Praxis sind von Anfang an verzahnt.",
      },
    ],
  },
  {
    slug: "praktisches-jahr",
    term: "Praktisches Jahr (PJ)",
    category: "Studium",
    short: "Letztes Studienjahr mit ganztägiger Arbeit in der Klinik.",
    definition:
      "Das Praktische Jahr ist der letzte Abschnitt des Medizinstudiums. Über rund 48 Wochen arbeiten Studierende ganztägig in Kliniken mit und wenden ihr Wissen unter ärztlicher Anleitung praktisch an. Danach folgt das abschließende dritte Staatsexamen und die Approbation.",
    seoTitle: "Praktisches Jahr (PJ) im Medizinstudium | ncrechner",
    metaDescription:
      "Praktisches Jahr erklärt: rund 48 Wochen in drei Tertialen (Innere, Chirurgie, Wahlfach), wann es beginnt, wo es stattfindet, die Aufwandsentschädigung.",
    heroImage: "/images/blog/theme-hospital-corridor.jpg",
    heroImageAlt: "Studierende im Praktischen Jahr auf der Klinikstation",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was das PJ ist",
        paragraphs: [
          "Im Praktischen Jahr wechseln Studierende vom Lernen zum Mitarbeiten: Sie übernehmen unter Aufsicht ärztliche Aufgaben wie Anamnese, Untersuchungen und Blutentnahmen und sind fest in den Stationsalltag eingebunden.",
          "Es ist der Übergang vom Studium in den ärztlichen Berufsalltag.",
        ],
      },
      {
        id: "aufbau",
        heading: "Dauer und Aufbau",
        paragraphs: [
          "Das PJ dauert rund 48 Wochen und gliedert sich in drei Tertiale zu je etwa 16 Wochen: Innere Medizin und Chirurgie sind Pflicht, das dritte Tertial ist ein frei wählbares Fach.",
          "Für Fehlzeiten gelten feste Obergrenzen über die gesamte Zeit und je Tertial.",
        ],
      },
      {
        id: "zeitpunkt-ort",
        heading: "Zeitpunkt und Orte",
        paragraphs: [
          "Das PJ beginnt nach dem zweiten Staatsexamen und endet vor dem dritten. Absolvieren kannst du es an Universitätskliniken, Lehrkrankenhäusern oder Lehrpraxen, unter bestimmten Bedingungen auch im Ausland.",
          "Auslandsabschnitte brauchen deutlich mehr Vorlauf bei der Planung.",
        ],
      },
      {
        id: "verguetung",
        heading: "Vergütung und Bedeutung",
        paragraphs: [
          "Für das PJ gibt es je nach Einrichtung eine Aufwandsentschädigung. Fachlich ist es prägend: Viele finden hier ihre spätere Fachrichtung und knüpfen Kontakte für die Weiterbildung.",
          "Nach bestandenem dritten Staatsexamen kann die Approbation beantragt werden.",
        ],
      },
    ],
    related: ["famulatur", "physikum", "klinik"],
    faqs: [
      {
        question: "Wie lange dauert das PJ?",
        answer:
          "Rund 48 Wochen, aufgeteilt in drei Tertiale zu je etwa 16 Wochen. Innere Medizin und Chirurgie sind verpflichtend, das dritte Tertial ist ein frei wählbares Fach.",
      },
      {
        question: "Wann beginnt das Praktische Jahr?",
        answer:
          "Nach bestandenem zweitem Staatsexamen und vor dem dritten. Es ist der letzte Studienabschnitt, an dessen Ende die Approbation als Ärztin oder Arzt steht.",
      },
      {
        question: "Wird das PJ vergütet?",
        answer:
          "Ja, je nach Einrichtung in Form einer Aufwandsentschädigung. Die Höhe unterscheidet sich zwischen den Kliniken und ist nicht mit einem regulären Gehalt vergleichbar.",
      },
    ],
  },
  {
    slug: "modellstudiengang",
    term: "Modellstudiengang",
    category: "Studium",
    short: "Studienmodell, das Theorie und Praxis von Anfang an verzahnt.",
    definition:
      "Ein Modellstudiengang ist eine moderne Struktur des Medizinstudiums, die theoretisches und praktisches Lernen von Anfang an verbindet. Die strikte Trennung zwischen Vorklinik und Klinik entfällt, gelehrt wird themen- und fallbezogen, häufig ohne klassisches Physikum in der bekannten Form.",
    seoTitle: "Modellstudiengang Medizin erklärt | ncrechner",
    metaDescription:
      "Modellstudiengang erklärt: früher Patientenkontakt, integrierte Module statt Vorklinik-Klinik-Trennung, welche Unis ihn anbieten, Vor- und Nachteile.",
    heroImage: "/images/blog/theme-study-books.jpg",
    heroImageAlt: "Fall- und praxisorientiertes Lernen im Modellstudiengang Medizin",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was ein Modellstudiengang ist",
        paragraphs: [
          "Der Modellstudiengang verzahnt Theorie und Praxis von Beginn an und ersetzt die klassische Trennung in vorklinischen und klinischen Abschnitt. Statt isolierter Fächer stehen fächerübergreifende, themenbezogene Module im Mittelpunkt.",
          "Patientenkontakt und anwendungsnahes Lernen setzen deutlich früher ein als im klassischen Aufbau.",
        ],
      },
      {
        id: "unterschied",
        heading: "Unterschied zum Regelstudiengang",
        paragraphs: [
          "Im Regelstudiengang folgt auf zwei Jahre Grundlagen das Physikum, danach der klinische Teil. Im Modellstudiengang gibt es diesen Schnitt so nicht; geprüft wird oft kontinuierlich über den Verlauf.",
          "Beide führen zum selben Abschluss und denselben Staatsexamina, unterscheiden sich aber im Aufbau und im Zeitpunkt des Praxisbezugs.",
        ],
      },
      {
        id: "unis",
        heading: "Wo es angeboten wird",
        paragraphs: [
          "Mehrere Hochschulen setzen auf Modellstudiengänge, etwa die Charité in Berlin, die Medizinische Hochschule Hannover, die Ruhr-Universität Bochum und die Universität Hamburg. Die konkrete Ausgestaltung unterscheidet sich je Standort.",
          "Für die Studienwahl lohnt deshalb der genaue Blick, ob und wie ein Standort das Modell umsetzt.",
        ],
      },
      {
        id: "fuer-wen",
        heading: "Vor- und Nachteile",
        paragraphs: [
          "Vorteile sind früher Patientenkontakt, kleinere Gruppen und ein enger Praxisbezug. Nachteile sind das begrenzte Platzangebot, die dichtere Struktur und der teils erschwerte Hochschulwechsel.",
          "Der Modellstudiengang passt zu praxis- und forschungsorientierten Studierenden; wer klare, standardisierte Strukturen bevorzugt, ist im Regelstudiengang oft besser aufgehoben.",
        ],
      },
    ],
    related: ["modellstudiengang", "vorklinik", "physikum"],
    faqs: [
      {
        question: "Was unterscheidet Modell- vom Regelstudiengang?",
        answer:
          "Der Modellstudiengang verzahnt Theorie und Praxis von Anfang an und verzichtet auf die strikte Vorklinik-Klinik-Trennung, oft ohne klassisches Physikum. Der Regelstudiengang trennt beide Abschnitte klar.",
      },
      {
        question: "Ist der Abschluss gleichwertig?",
        answer:
          "Ja. Beide Wege führen zu denselben Staatsexamina und zur Approbation. Sie unterscheiden sich nur im Aufbau und im Zeitpunkt, ab dem der Praxisbezug einsetzt.",
      },
      {
        question: "Für wen eignet sich der Modellstudiengang?",
        answer:
          "Für praxis- und forschungsnah Lernende, die früh Patientenkontakt schätzen. Wer klare, standardisierte Strukturen und einen einfacheren Hochschulwechsel bevorzugt, fährt mit dem Regelstudiengang oft besser.",
      },
    ],
  },
  {
    slug: "ablehnungsbescheid",
    term: "Ablehnungsbescheid",
    category: "Bewerbung",
    short: "Die Mitteilung, dass es in dieser Runde nicht für einen Platz reichte.",
    definition:
      "Ein Ablehnungsbescheid teilt dir mit, dass deine Bewerbung in einer Vergaberunde nicht zu einem Studienplatz geführt hat. Er ist das Gegenstück zum Zulassungsbescheid – aber kein endgültiges Aus: Häufig läuft deine Bewerbung über Nachrücken weiter, und für den nächsten Anlauf gibt es klare Stellschrauben.",
    seoTitle: "Ablehnungsbescheid Medizin: Was tun? | ncrechner",
    metaDescription:
      "Ablehnungsbescheid fürs Medizinstudium erhalten? Warum Absagen normal sind, was im Bescheid steht und welche Optionen (Nachrücken, TMS, Widerspruch) du jetzt.",
    heroImage: "/images/blog/b-absage.jpg",
    heroImageAlt:
      "Bewerberin liest nachdenklich einen Ablehnungsbescheid fürs Medizinstudium",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was ist ein Ablehnungsbescheid?",
        paragraphs: [
          "Ein Ablehnungsbescheid ist die offizielle Nachricht einer Hochschule oder der zentralen Vergabestelle, dass du in einer Runde keinen Studienplatz erhalten hast. Er ist das direkte Gegenstück zum Zulassungsbescheid und gehört im Medizinbereich zum Verfahren dazu.",
          "Das liegt an der Ausgangslage: Jahr für Jahr bewerben sich mehrere Zehntausend Menschen auf einige Tausend Plätze. Eine Absage ist deshalb kein Urteil über deine Eignung, sondern vor allem Ausdruck eines extrem knappen Angebots.",
        ],
      },
      {
        id: "gruende",
        heading: "Warum du eine Absage bekommst",
        paragraphs: [
          "Der häufigste Grund ist schlicht das Verhältnis von Bewerbern zu Plätzen. Selbst mit einem guten Profil reicht es nicht immer, weil in jeder Quote nur die vordersten Ränge zum Zug kommen.",
          "Daneben gibt es vermeidbare Ursachen: unvollständige Unterlagen, verpasste Fristen oder Formfehler im Portal. Und seit der Reform 2020 gibt es keine reine Wartezeitquote mehr – wer nur wartet, rückt nicht mehr automatisch auf.",
        ],
      },
      {
        id: "inhalt",
        heading: "Was im Bescheid steht",
        paragraphs: [
          "Ein Ablehnungsbescheid nennt in der Regel, in welcher Quote deine Bewerbung geprüft wurde und woran es gescheitert ist – oft mit dem Grenzwert oder Rang der Runde. Genau diese Angabe ist wertvoll, weil sie zeigt, wie weit dir noch fehlte.",
          "Wichtig ist außerdem die Rechtsbehelfsbelehrung am Ende: Sie erklärt, ob und in welcher Frist du Widerspruch einlegen kannst. Anders als der Zulassungsbescheid, der dir einen Platz anbietet, dokumentiert der Ablehnungsbescheid nur das Ergebnis dieser einen Runde.",
        ],
      },
      {
        id: "optionen",
        heading: "Deine Optionen nach der Absage",
        paragraphs: [
          "Eine Absage in der ersten Runde bedeutet nicht das Ende des Verfahrens. Mehrere Wege laufen parallel oder schließen direkt an:",
        ],
        list: [
          "Nachrücken und Losverfahren: Im selben Bewerbungsjahr werden frei werdende Plätze weiter vergeben.",
          "Profil gezielt stärken: Ein guter TMS, ein Freiwilligendienst oder eine Ausbildung wirken in AdH und ZEQ der nächsten Runde.",
          "Studium im Ausland: In Österreich über den MedAT, in Teilen Mittel- und Osteuropas über eigene Aufnahmeprüfungen.",
          "Private Hochschulen in Deutschland mit eigenen Auswahlverfahren.",
          "Quereinstieg über ein verwandtes Studienfach oder alternative Gesundheitsberufe als Zwischenschritt.",
        ],
      },
      {
        id: "widerspruch",
        heading: "Widerspruch und Studienplatzklage",
        paragraphs: [
          "Hältst du den Bescheid für fehlerhaft, kannst du innerhalb der in der Rechtsbehelfsbelehrung genannten Frist – meist rund ein Monat – Widerspruch einlegen. Dafür solltest du deine Nachweise wie Zeugnis, Testergebnisse und Erfahrungsnachweise vollständig beilegen.",
          "Bleibt der Widerspruch erfolglos, ist als letzter Schritt eine Studienplatzklage möglich. Sie ist allerdings aufwendig, mit Kosten verbunden und im Ausgang unsicher – sinnvoll nur nach nüchterner Abwägung des Einzelfalls.",
        ],
      },
      {
        id: "einordnung",
        heading: "Absage einordnen: normal, nicht endgültig",
        paragraphs: [
          "Angesichts der Bewerberzahlen ist eine Absage der Normalfall, nicht die Ausnahme. Entscheidend ist, was du daraus machst: den Bescheid analysieren, die fehlenden Punkte identifizieren und den nächsten Anlauf gezielt planen.",
          "Am besten fährst du von Anfang an mehrgleisig. Mit der ncrechner App siehst du, an welchen Hochschulen und in welcher Quote dir wie viel gefehlt hat – und welcher Baustein deine Chancen im nächsten Jahr am stärksten hebt.",
        ],
      },
    ],
    related: ["nachruecken", "studienplatzklage", "losverfahren", "wartesemester"],
    article: {
      label: "Absage analysieren, zweiter Anlauf",
      href: "/ratgeber/bewerbung/absage-analysieren-zweiter-anlauf/",
    },
    faqs: [
      {
        question: "Ist ein Ablehnungsbescheid endgültig?",
        answer:
          "Nein. Er betrifft nur diese eine Runde. Über Nachrücken und Losverfahren kann sich im selben Jahr noch ein Platz ergeben, und für die nächste Bewerbung lässt sich das Profil gezielt verbessern.",
      },
      {
        question: "Wie lange habe ich Zeit für einen Widerspruch?",
        answer:
          "Maßgeblich ist die Frist in der Rechtsbehelfsbelehrung des Bescheids, meist rund ein Monat. Versäumst du sie, wird der Bescheid bestandskräftig. Reagiere deshalb sofort nach Erhalt.",
      },
      {
        question: "Was ist der Unterschied zum Zulassungsbescheid?",
        answer:
          "Der Zulassungsbescheid bietet dir einen Studienplatz an, der Ablehnungsbescheid dokumentiert eine Absage in dieser Runde. Beide nennen Fristen – beim Ablehnungsbescheid für einen möglichen Widerspruch.",
      },
      {
        question: "Lohnt sich eine Studienplatzklage nach der Absage?",
        answer:
          "Das hängt vom Einzelfall ab. Eine Klage ist aufwendig, teuer und im Ergebnis unsicher. Sie ist eine ergänzende Option, ersetzt aber keine Strategie über die regulären Quoten.",
      },
      {
        question: "Was sollte ich direkt nach der Absage tun?",
        answer:
          "Prüfe, in welcher Quote und mit welchem Wert es knapp wurde, und leite daraus den nächsten Schritt ab: Nachrücken abwarten, TMS oder einen Dienst einplanen und deine Ortspräferenzen für die nächste Runde anpassen.",
      },
    ],
  },
  {
    slug: "anerkannter-dienst",
    term: "Anerkannter Dienst",
    category: "Bewerbung",
    short: "Dienstzeiten wie FSJ oder BFD, die in AdH und ZEQ Punkte bringen.",
    definition:
      "Als anerkannter Dienst gelten Freiwilligendienste wie FSJ, FÖJ oder der Bundesfreiwilligendienst, die viele Hochschulen im Auswahlverfahren der Hochschulen (AdH) und in der Zusätzlichen Eignungsquote (ZEQ) mit Punkten werten. Üblich ist eine Mindestdauer von rund elf bis zwölf Monaten am Stück.",
    seoTitle: "Anerkannter Dienst: FSJ & BFD für Medizin | ncrechner",
    metaDescription:
      "Anerkannter Dienst fürs Medizinstudium: Welche Freiwilligendienste zählen, in welchen Quoten sie Punkte bringen, welche Mindestdauer gilt und welche Fristen.",
    heroImage: "/images/blog/b-dienst-rettungsdienst.jpg",
    heroImageAlt: "Freiwillige im Rettungsdienst – ein anerkannter Dienst für die Medizin-Bewerbung",
    body: [],
    sections: [
      {
        id: "was-gilt",
        heading: "Was als anerkannter Dienst gilt",
        paragraphs: [
          "Anerkannt sind vor allem geregelte Freiwilligendienste: das Freiwillige Soziale Jahr (FSJ), das Freiwillige Ökologische Jahr (FÖJ) und der Bundesfreiwilligendienst (BFD). Auch internationale Freiwilligendienste und der Wehrdienst können je nach Hochschule zählen.",
          "Wichtig ist der Rahmen: Der Dienst muss bei einer anerkannten Einrichtung geleistet werden, etwa bei DRK, Malteser, Johanniter, THW, Feuerwehr oder in Kliniken und Pflegeeinrichtungen.",
        ],
      },
      {
        id: "quoten",
        heading: "In welchen Quoten der Dienst zählt",
        paragraphs: [
          "Punkte bringt ein anerkannter Dienst im AdH und in der ZEQ. In der Abiturbestenquote spielt er keine Rolle, weil dort allein die Note entscheidet.",
          "Wie stark er gewichtet wird, legt jede Hochschule fest – an manchen Standorten sind dafür mehrere Punkte in der AdH-Wertung vorgesehen.",
        ],
      },
      {
        id: "dauer",
        heading: "Mindestdauer und Bedingungen",
        paragraphs: [
          "Üblich ist eine zusammenhängende Mindestdauer von rund elf bis zwölf Monaten. Kürzere Einsätze werden häufig nicht für die volle Wertung anerkannt.",
          "Der Dienst muss in der Regel abgeschlossen und bis zur Bewerbungsfrist nachgewiesen sein – Ende Januar fürs Sommer-, Ende Juli fürs Wintersemester.",
        ],
      },
      {
        id: "einsatzbereiche",
        heading: "Anerkannte Einsatzbereiche",
        paragraphs: [
          "Die anerkannten Bereiche gehen über die reine Medizin hinaus.",
        ],
        list: [
          "Gesundheit und Pflege, Kliniken und Rettungsdienst",
          "Kinder- und Jugendarbeit, Arbeit mit Menschen mit Behinderung",
          "Katastrophen- und Zivilschutz",
          "Soziale Einrichtungen und Schulen",
        ],
      },
      {
        id: "warum",
        heading: "Warum sich ein Dienst lohnt",
        paragraphs: [
          "Der Dienst ist der am schnellsten erarbeitbare Pluspunkt und zugleich ein ehrlicher Praxistest: Ein Jahr auf Station, Rettungswache oder in der Pflege zeigt zuverlässig, ob Medizin dein Weg ist.",
          "Mit der ncrechner App siehst du, an welchen Hochschulen ein Dienst wie viel bringt und wie er sich mit einem TMS kombinieren lässt.",
        ],
      },
    ],
    related: ["freiwilligendienst", "anerkannte-berufstaetigkeit", "berufsausbildung", "zusaetzliche-eignungsquote"],
    article: {
      label: "Dienst wählen: FSJ, BFD, Rettungsdienst",
      href: "/ratgeber/bewerbung/dienst-waehlen-fsj-bfd-rettungsdienst/",
    },
    faqs: [
      {
        question: "Wie lange muss ein anerkannter Dienst dauern?",
        answer:
          "Meist rund elf bis zwölf Monate am Stück. Kürzere Dienste werden häufig nicht voll gewertet. Prüfe die genaue Mindestdauer in der Zulassungsordnung deiner Ziel-Hochschulen.",
      },
      {
        question: "Zählt ein Dienst in der Abiturbestenquote?",
        answer:
          "Nein. In der ABQ zählt ausschließlich die Abiturnote. Ein anerkannter Dienst wirkt nur im Auswahlverfahren der Hochschulen (AdH) und in der Zusätzlichen Eignungsquote (ZEQ).",
      },
      {
        question: "Welche Dienste werden anerkannt?",
        answer:
          "Vor allem FSJ, FÖJ und der Bundesfreiwilligendienst, geleistet bei anerkannten Einrichtungen wie DRK, Malteser, Johanniter, THW oder in Kliniken. Teils zählen auch internationale Freiwilligendienste.",
      },
      {
        question: "Muss der Dienst abgeschlossen sein?",
        answer:
          "In der Regel ja, und er muss bis zur Bewerbungsfrist nachgewiesen werden – Ende Januar fürs Sommer-, Ende Juli fürs Wintersemester. Plane den Abschluss deshalb rechtzeitig vor der Frist.",
      },
    ],
  },
  {
    slug: "anerkannte-berufstaetigkeit",
    term: "Anerkannte Berufstätigkeit",
    category: "Bewerbung",
    short: "Einschlägige Berufserfahrung, die in AdH und ZEQ Punkte bringen kann.",
    definition:
      "Als anerkannte Berufstätigkeit zählt eine einschlägige, meist mindestens zwölfmonatige Tätigkeit in einem medizinnahen Beruf, in der Regel nach einer passenden abgeschlossenen Ausbildung. Viele Hochschulen werten sie im Auswahlverfahren der Hochschulen (AdH) und in der Zusätzlichen Eignungsquote (ZEQ).",
    seoTitle: "Anerkannte Berufstätigkeit für Medizin | ncrechner",
    metaDescription:
      "Anerkannte Berufstätigkeit fürs Medizinstudium: Wie sie sich von der Ausbildung unterscheidet, in welchen Quoten sie zählt, welche Dauer und Nachweise nötig.",
    heroImage: "/images/blog/b-berufstaetigkeit.jpg",
    heroImageAlt: "Pflegekraft bei der Arbeit – anerkannte Berufstätigkeit für die Medizin-Bewerbung",
    body: [],
    sections: [
      {
        id: "was-bedeutet",
        heading: "Was anerkannte Berufstätigkeit bedeutet",
        paragraphs: [
          "Gemeint ist qualifizierte, einschlägige Berufserfahrung in einem medizin- oder pflegenahen Beruf. Sie folgt in der Regel auf eine passende Ausbildung: Erst der Abschluss, dann die praktische Tätigkeit im selben Feld.",
          "Zusammen ergeben Ausbildung und anschließende Berufstätigkeit ein besonders starkes, praxisnahes Profil.",
        ],
      },
      {
        id: "abgrenzung",
        heading: "Abgrenzung zur Ausbildung",
        paragraphs: [
          "Die abgeschlossene Ausbildung ist der formale Nachweis der Qualifikation. Die anerkannte Berufstätigkeit ist die Zeit danach, in der du in diesem Beruf tatsächlich gearbeitet hast.",
          "Manche Hochschulen werten beides getrennt, andere verlangen die Berufstätigkeit zusätzlich zur Ausbildung. Der genaue Zuschnitt steht in der jeweiligen Zulassungsordnung.",
        ],
      },
      {
        id: "quoten",
        heading: "In welchen Quoten sie zählt",
        paragraphs: [
          "Anerkannte Berufstätigkeit wirkt im AdH und in der ZEQ. In der Abiturbestenquote spielt sie keine Rolle, weil dort ausschließlich die Note zählt.",
          "Besonders in der notenunabhängigen ZEQ kann einschlägige Berufserfahrung die Chancen deutlich verbessern.",
        ],
      },
      {
        id: "dauer-nachweis",
        heading: "Dauer, Fristen und Nachweise",
        paragraphs: [
          "Häufig ist eine Mindestdauer von rund zwölf Monaten einschlägiger Tätigkeit gefordert. Nachgewiesen wird sie über Ausbildungszeugnis und Arbeitsnachweise wie Arbeitsverträge oder Bescheinigungen des Arbeitgebers.",
          "Wie beim übrigen Verfahren gelten die Fristen Ende Januar fürs Sommer- und Ende Juli fürs Wintersemester.",
        ],
      },
      {
        id: "welche-berufe",
        heading: "Welche Berufe zählen",
        paragraphs: [
          "Anerkannt wird ein breites Spektrum an Gesundheits- und Pflegeberufen. Welche genau und wie stark, entscheidet jede Hochschule selbst.",
        ],
        list: [
          "Pflegeberufe und Hebammenwesen",
          "Therapieberufe wie Physio-, Ergotherapie und Logopädie",
          "Diagnostik- und Laborberufe (MTA, Laborberufe)",
          "Rettungsdienst (Notfallsanitäter, Rettungsassistenz)",
        ],
      },
    ],
    related: ["berufsausbildung", "anerkannter-dienst", "zusaetzliche-eignungsquote", "freiwilligendienst"],
    article: {
      label: "Berufsausbildung & Berufstätigkeit",
      href: "/ratgeber/zulassung/berufsausbildung-berufstaetigkeit-medizin/",
    },
    faqs: [
      {
        question: "Was ist der Unterschied zur Ausbildung?",
        answer:
          "Die Ausbildung ist der Abschluss, die Berufstätigkeit die anschließende Arbeit im selben Beruf. Viele Hochschulen berücksichtigen die Tätigkeit zusätzlich zur abgeschlossenen Ausbildung.",
      },
      {
        question: "Wie lange muss ich gearbeitet haben?",
        answer:
          "Oft mindestens rund zwölf Monate einschlägiger Tätigkeit. Die genaue Dauer variiert je Hochschule, deshalb lohnt der Blick in die jeweilige Zulassungsordnung.",
      },
      {
        question: "In welchen Quoten zählt Berufstätigkeit?",
        answer:
          "Im Auswahlverfahren der Hochschulen (AdH) und in der Zusätzlichen Eignungsquote (ZEQ). In der Abiturbestenquote zählt sie nicht, weil dort allein die Abiturnote entscheidet.",
      },
      {
        question: "Welche Nachweise brauche ich?",
        answer:
          "In der Regel das Ausbildungszeugnis plus Arbeitsnachweise wie Arbeitsvertrag oder eine Bescheinigung des Arbeitgebers über Art und Dauer der Tätigkeit, fristgerecht zur Bewerbung eingereicht.",
      },
    ],
  },
  {
    slug: "anerkannter-preis",
    term: "Anerkannter Preis",
    category: "Quoten",
    short: "Auszeichnungen aus anerkannten Wettbewerben als Auswahlkriterium.",
    definition:
      "Ein anerkannter Preis ist eine Auszeichnung aus einem klar definierten, einschlägigen Wettbewerb, die Hochschulen im Auswahlverfahren der Hochschulen (AdH) und in der Zusätzlichen Eignungsquote (ZEQ) werten. Typisch sind Erfolge bei bundesweiten Wissenschafts- und Fachwettbewerben – nicht jede Teilnahme zählt.",
    seoTitle: "Anerkannter Preis: Wettbewerbe für Medizin | ncrechner",
    metaDescription:
      "Anerkannter Preis fürs Medizinstudium: Welche Wettbewerbe zählen, in welchen Quoten ein Preis Punkte bringt, wie stark er wirkt und wie du ihn nachweist.",
    heroImage: "/images/blog/b-wettbewerbe.jpg",
    heroImageAlt: "Preisverleihung bei einem Wissenschaftswettbewerb als Auswahlkriterium fürs Medizinstudium",
    body: [],
    sections: [
      {
        id: "was-gilt",
        heading: "Was als anerkannter Preis gilt",
        paragraphs: [
          "Ein anerkannter Preis ist eine Auszeichnung in einem bildungsbezogenen Wettbewerb, den Hochschulen ausdrücklich als besonderen Eignungsnachweis akzeptieren. Entscheidend ist nicht die Teilnahme, sondern eine tatsächliche Platzierung oder Auszeichnung.",
          "Vergeben werden solche Preise etwa von Wettbewerbsträgern, Fachgesellschaften oder Stiftungen im naturwissenschaftlichen Bereich.",
        ],
      },
      {
        id: "welche-wettbewerbe",
        heading: "Welche Wettbewerbe anerkannt werden",
        paragraphs: [
          "Anerkannt sind in der Regel renommierte, bundesweite Wettbewerbe mit naturwissenschaftlichem Bezug. Die genaue Liste legt jede Hochschule fest.",
        ],
        list: [
          "Science-Olympiaden in Biologie, Chemie, Physik, Mathematik und Informatik",
          "Jugend forscht, meist ein erster bis dritter Preis auf Landes- oder Bundesebene",
          "Weitere anerkannte Fachwettbewerbe in Naturwissenschaft und Technik",
        ],
      },
      {
        id: "quoten",
        heading: "In welchen Quoten ein Preis zählt",
        paragraphs: [
          "Ein anerkannter Preis wirkt vor allem im AdH und in der ZEQ. In einzelnen Bundesländern kann er zusätzlich in der Landarztquote berücksichtigt werden.",
          "In der Abiturbestenquote gibt es keine Punkte dafür – dort zählt allein die Note. Ein Preis kann die Bewerbung aber in den anderen Quoten spürbar aufwerten.",
        ],
      },
      {
        id: "wie-stark",
        heading: "Wie stark ein Preis wirkt",
        paragraphs: [
          "Ein anerkannter Preis kann ein deutlicher Vorteil sein, besonders wenn die Note allein nicht reicht und die Hochschule Zusatzleistungen hoch gewichtet.",
          "Er ersetzt aber selten die großen Hebel: Ein guter TMS oder ein Dienst wiegt in den meisten Verfahren schwerer. Am besten wirkt ein Preis als ergänzender Baustein eines starken Gesamtprofils.",
        ],
      },
      {
        id: "nachweis",
        heading: "Nachweis und Unterschiede je Hochschule",
        paragraphs: [
          "Nachgewiesen wird ein Preis über die Urkunde oder eine offizielle Bescheinigung des Wettbewerbsträgers. Welche Wettbewerbe anerkannt werden und wie viele Punkte sie bringen, unterscheidet sich je Hochschule und Bundesland.",
          "Mit der ncrechner App siehst du, an welchen Hochschulen ein anerkannter Preis überhaupt gewertet wird und wie er dein Profil verändert.",
        ],
      },
    ],
    related: ["zusaetzliche-eignungsquote", "auswahlverfahren-der-hochschulen", "talentquote"],
    article: {
      label: "Wettbewerbe & Preise in der Zulassung",
      href: "/ratgeber/zulassung/wettbewerbe-preise-medizin-zulassung/",
    },
    faqs: [
      {
        question: "Welche Wettbewerbe zählen als anerkannter Preis?",
        answer:
          "Vor allem renommierte, bundesweite Naturwissenschaftswettbewerbe wie die Science-Olympiaden (Biologie, Chemie, Physik, Mathematik, Informatik) oder Jugend forscht. Die genaue Liste legt jede Hochschule in ihrer Zulassungsordnung fest.",
      },
      {
        question: "In welchen Quoten hilft ein Preis?",
        answer:
          "Vor allem im AdH und in der ZEQ, in einzelnen Ländern auch in der Landarztquote. In der Abiturbestenquote zählt er nicht, weil dort ausschließlich die Note entscheidet.",
      },
      {
        question: "Zählt jede Teilnahme?",
        answer:
          "Nein. Gewertet wird nur eine tatsächliche Auszeichnung oder Platzierung in einem anerkannten Wettbewerb, nicht die bloße Teilnahme. Welche Preise zählen, ist je Hochschule unterschiedlich.",
      },
      {
        question: "Wie weise ich einen Preis nach?",
        answer:
          "Über die Urkunde oder eine offizielle Bescheinigung des Wettbewerbsträgers, fristgerecht zur Bewerbung eingereicht. Reiche den Nachweis nur ein, wenn deine Ziel-Hochschule den Wettbewerb anerkennt.",
      },
    ],
  },
  {
    slug: "bafoeg",
    term: "BAföG",
    category: "Studium",
    apoBankCta: "studienkredit",
    short: "Staatliche Studienförderung – halb Zuschuss, halb zinsloses Darlehen.",
    definition:
      "BAföG ist die staatliche Förderung nach dem Bundesausbildungsförderungsgesetz, mit der Studierende ihren Lebensunterhalt finanzieren können, wenn die Mittel der Familie nicht ausreichen. Die Hälfte ist ein Zuschuss, die andere ein zinsloses Darlehen. Es ist keine Zulassungsvoraussetzung, aber für die Finanzierung zentral.",
    seoTitle: "BAföG im Medizinstudium: Förderung erklärt | ncrechner",
    metaDescription:
      "BAföG erklärt: wer Anspruch hat, wie sich Zuschuss und zinsloses Darlehen aufteilen, wie der Betrag berechnet wird und was fürs lange Medizinstudium.",
    heroImage: "/images/blog/b-kosten.jpg",
    heroImageAlt: "Studienfinanzierung per BAföG – Symbol für Kosten und Förderung",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was BAföG ist",
        paragraphs: [
          "BAföG soll einen möglichst chancengleichen Zugang zum Studium sichern, unabhängig vom Geldbeutel der Familie. Die Förderung teilt sich in zwei Hälften: einen Zuschuss, den du nicht zurückzahlst, und ein zinsloses Darlehen mit gedeckelter Rückzahlung.",
          "Für die Zulassung spielt BAföG keine Rolle. Es geht ausschließlich um die Finanzierung während des Studiums.",
        ],
      },
      {
        id: "anspruch",
        heading: "Wer Anspruch hat",
        paragraphs: [
          "Ob und wie viel dir zusteht, hängt vor allem vom Einkommen deiner Eltern, deinem eigenen Einkommen und Vermögen sowie deiner Wohnsituation ab. Es gelten außerdem Altersgrenzen.",
          "Eigenes Einkommen bis zu einem Freibetrag bleibt anrechnungsfrei; darüber wird die Förderung gekürzt.",
        ],
      },
      {
        id: "dauer",
        heading: "Dauer und Nachweise",
        paragraphs: [
          "Gefördert wird grundsätzlich die Regelstudienzeit. Nach den ersten Semestern musst du einen Leistungsnachweis erbringen, um die Förderung fortzusetzen.",
          "Anträge stellst du beim zuständigen Amt, idealerweise vor Studienbeginn, da die Bearbeitung einige Wochen dauern kann.",
        ],
      },
      {
        id: "medizin",
        heading: "Warum BAföG fürs Medizinstudium zählt",
        paragraphs: [
          "Das Medizinstudium ist lang und dicht getaktet, mit wenig Raum für Nebenjobs. Eine verlässliche Grundfinanzierung nimmt Druck und schafft Zeit fürs Lernen.",
          "Reicht das BAföG nicht, kommen Stipendien, Studienkredite oder Werkstudententätigkeiten als Ergänzung infrage.",
        ],
      },
    ],
    related: ["hochschulzugangsberechtigung", "vorklinik", "physikum"],
    faqs: [
      {
        question: "Muss ich BAföG zurückzahlen?",
        answer:
          "Nur die Hälfte. BAföG besteht je zur Hälfte aus einem Zuschuss, den du behältst, und einem zinslosen Darlehen mit gedeckelter Rückzahlung, die erst einige Jahre nach dem Studium beginnt.",
      },
      {
        question: "Beeinflusst BAföG meine Zulassung?",
        answer:
          "Nein. BAföG ist reine Studienfinanzierung und spielt im Vergabeverfahren keine Rolle. Über die Zulassung entscheiden Note, Test und die Kriterien der Quoten.",
      },
      {
        question: "Wann sollte ich BAföG beantragen?",
        answer:
          "Am besten vor oder gleich zu Studienbeginn, weil die Bearbeitung mehrere Wochen dauern kann. Ein früher Antrag verhindert Finanzierungslücken in den ersten Monaten.",
      },
    ],
  },
  {
    slug: "ems-eignungstest",
    term: "EMS (Eignungstest für das Medizinstudium)",
    category: "Tests",
    short: "Der Aufnahmetest der medizinischen Fakultäten in der Schweiz.",
    definition:
      "Der EMS ist der Eignungstest, über den mehrere Schweizer Universitäten den Zugang zum Medizinstudium steuern. Er prüft in vielen Untertests kognitive Fähigkeiten und ist dem deutschen TMS eng verwandt, wird aber eigenständig organisiert und rein relativ zu den Mitbewerbern bewertet.",
    seoTitle: "EMS Schweiz: Eignungstest fürs Medizinstudium | ncrechner",
    metaDescription:
      "EMS erklärt: der Schweizer Aufnahmetest fürs Medizinstudium, was er prüft, wie er abläuft, wie er sich vom TMS unterscheidet und was für deutsche Bewerber gilt.",
    heroImage: "/images/blog/t-vorbereitung-plan.jpg",
    heroImageAlt: "Vorbereitung auf den EMS-Eignungstest für das Medizinstudium in der Schweiz",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was der EMS ist",
        paragraphs: [
          "Der EMS ist ein standardisierter Eignungstest für zulassungsbeschränkte Medizinstudiengänge in der Schweiz. Er wird zentral entwickelt und an mehreren Universitäten als zentrales Auswahlinstrument eingesetzt.",
          "Anders als eine Note misst er allein kognitive Leistungsfähigkeit am Testtag.",
        ],
      },
      {
        id: "inhalt",
        heading: "Was er prüft",
        paragraphs: [
          "Der EMS deckt ein breites Spektrum ab: Mustererkennung und räumliches Denken, medizinisch-naturwissenschaftliches Grundverständnis, logisches und quantitatives Denken, Textverständnis sowie Konzentrations- und Gedächtnisaufgaben.",
          "Er besteht aus vielen Aufgaben über mehrere Stunden und wird an einem einzigen Termin im Jahr, meist im Juli, geschrieben.",
        ],
      },
      {
        id: "deutsche-bewerber",
        heading: "Was für deutsche Bewerber gilt",
        paragraphs: [
          "Der EMS dient in erster Linie der Schweizer Zulassung und ist ein eigener Weg neben dem deutschen NC-System. Deutsche Bewerber können sich bewerben, müssen den Test aber ablegen.",
          "Die Plätze sind knapp und die Auswahl rein leistungsabhängig, weil das Ergebnis relativ zu allen Teilnehmenden gewertet wird.",
        ],
      },
      {
        id: "vs-tms",
        heading: "EMS oder TMS?",
        paragraphs: [
          "Beide Tests ähneln sich inhaltlich, verfolgen aber verschiedene Zwecke: Der TMS verbessert die Chancen im deutschen Verfahren, der EMS ist das Auswahlinstrument der Schweiz. Der TMS wird mehrfach im Jahr angeboten, der EMS nur einmal.",
          "Wer beide Länder in Betracht zieht, sollte die Termine früh abgleichen und die Vorbereitung darauf abstimmen.",
        ],
      },
    ],
    related: ["studierfaehigkeitstest", "tms", "medat"],
    faqs: [
      {
        question: "Zählt der EMS auch in Deutschland?",
        answer:
          "Nein. Der EMS ist das Auswahlinstrument Schweizer Universitäten. Für die deutsche Zulassung ist der TMS relevant. Beide Tests sind sich inhaltlich ähnlich, aber getrennte Verfahren.",
      },
      {
        question: "Wie oft kann ich den EMS ablegen?",
        answer:
          "Der EMS findet einmal jährlich statt und lässt sich in Folgejahren wiederholen. Weil es nur einen Termin gibt, ist eine gründliche Vorbereitung auf diesen Tag besonders wichtig.",
      },
      {
        question: "Worin unterscheidet sich der EMS vom TMS?",
        answer:
          "Inhaltlich kaum, im Zweck deutlich: Der EMS entscheidet über die Schweizer Zulassung und wird relativ bewertet, der TMS stärkt die Bewerbung im deutschen System und wird in Punkten angegeben.",
      },
    ],
  },
  {
    slug: "hammerexamen",
    term: "Hammerexamen (Zweites Staatsexamen)",
    category: "Studium",
    short: "Die große schriftliche Prüfung am Ende der Klinik, vor dem PJ.",
    definition:
      "Als Hammerexamen wird umgangssprachlich der Zweite Abschnitt der Ärztlichen Prüfung bezeichnet – eine umfangreiche schriftliche Prüfung am Ende des klinischen Studienabschnitts. Der Spitzname spielt auf den enormen Stoffumfang an, der über mehrere Tage abgefragt wird.",
    seoTitle: "Hammerexamen (2. Staatsexamen) erklärt | ncrechner",
    metaDescription:
      "Hammerexamen erklärt: Was das zweite Staatsexamen ist, wann es stattfindet, wie es aufgebaut ist, welchen Stoff es abdeckt und wie es in die Reihe.",
    heroImage: "/images/blog/theme-study-books.jpg",
    heroImageAlt: "Lernunterlagen zur Vorbereitung auf das Hammerexamen",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was das Hammerexamen ist",
        paragraphs: [
          "Das Hammerexamen ist der zweite Abschnitt der Ärztlichen Prüfung und schließt den klinischen Studienabschnitt ab. Es prüft breit gefächertes klinisches Wissen über nahezu alle Fächer.",
          "Der Spitzname deutet an, wie umfangreich der Prüfungsstoff ist – viele empfinden es als eine der größten Hürden des Studiums.",
        ],
      },
      {
        id: "zeitpunkt",
        heading: "Wann es stattfindet",
        paragraphs: [
          "Es liegt nach den klinischen Semestern und vor dem Praktischen Jahr. Voraussetzung sind das bestandene Physikum, die klinischen Kurse und die abgeleisteten Famulaturen.",
          "Nach dem PJ folgt schließlich der dritte, mündlich-praktische Abschnitt als letztes Staatsexamen.",
        ],
      },
      {
        id: "aufbau",
        heading: "Aufbau und Stoff",
        paragraphs: [
          "Der schriftliche Teil besteht aus sehr vielen Multiple-Choice-Fragen, verteilt über mehrere aufeinanderfolgende Tage. Schwerpunkte liegen unter anderem auf Innerer Medizin und Chirurgie, ergänzt um viele weitere klinische Fächer.",
          "Zum Bestehen ist ein festgelegter Anteil richtiger Antworten nötig; typisch ist eine Vorbereitungsphase von mehreren Monaten.",
        ],
      },
      {
        id: "einordnung",
        heading: "Einordnung in die Staatsexamina",
        paragraphs: [
          "Die Reihenfolge ist klar: erst das Physikum als erster Abschnitt, dann das Hammerexamen als zweiter, schließlich der dritte Abschnitt nach dem PJ.",
          "Erst nach allen Abschnitten und dem PJ kann die Approbation beantragt werden.",
        ],
      },
    ],
    related: ["physikum", "praktisches-jahr", "klinik"],
    faqs: [
      {
        question: "Warum heißt es Hammerexamen?",
        answer:
          "Der Begriff ist umgangssprachlich und spielt auf den enormen Prüfungsstoff an. Offiziell ist es der Zweite Abschnitt der Ärztlichen Prüfung am Ende des klinischen Studienabschnitts.",
      },
      {
        question: "Wann findet das Hammerexamen statt?",
        answer:
          "Nach den klinischen Semestern und vor dem Praktischen Jahr. Voraussetzung sind das Physikum, die klinischen Kurse und die abgeleisteten Famulaturen.",
      },
      {
        question: "Wie ist das Hammerexamen aufgebaut?",
        answer:
          "Als umfangreiche schriftliche Prüfung mit sehr vielen Multiple-Choice-Fragen über mehrere Tage. Zum Bestehen ist ein festgelegter Anteil richtiger Antworten erforderlich.",
      },
    ],
  },
  {
    slug: "klinik",
    term: "Klinik (klinischer Studienabschnitt)",
    category: "Studium",
    short: "Der zweite Studienabschnitt mit Fokus auf Krankheitsbildern und Praxis.",
    definition:
      "Der klinische Abschnitt – kurz Klinik – ist der zweite große Teil des Medizinstudiums nach dem Physikum. Über mehrere Semester stehen Krankheitsbilder, Diagnostik und Therapie im Mittelpunkt, ergänzt durch Famulaturen und praktische Kurse am Patienten.",
    seoTitle: "Klinik: klinischer Studienabschnitt erklärt | ncrechner",
    metaDescription:
      "Der klinische Studienabschnitt (Klinik) erklärt: Wann er beginnt, wie lange er dauert, was gelehrt wird, welche Praxisanteile dazugehören und wie er sich.",
    heroImage: "/images/blog/theme-hospital-corridor.jpg",
    heroImageAlt: "Klinikflur als Symbol für den klinischen Studienabschnitt",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was die Klinik ist",
        paragraphs: [
          "Die Klinik ist der zweite große Studienabschnitt und verbindet theoretisches Wissen eng mit praktischen ärztlichen Fertigkeiten. Sie bereitet gezielt auf die spätere Arbeit als Ärztin oder Arzt vor.",
          "Sie beginnt nach bestandenem Physikum und erstreckt sich über mehrere Semester.",
        ],
      },
      {
        id: "inhalte",
        heading: "Was gelehrt wird",
        paragraphs: [
          "Im Zentrum stehen die großen klinischen Fächer wie Innere Medizin und Chirurgie sowie Mikrobiologie, Pharmakologie und Pathophysiologie. Diagnostik und Therapie konkreter Krankheitsbilder rücken in den Vordergrund.",
          "Geprüft wird in einer Mischung aus schriftlichen, mündlichen und praktischen Formaten.",
        ],
      },
      {
        id: "praxis",
        heading: "Praxisanteile",
        paragraphs: [
          "Skills-Labs trainieren Untersuchungstechniken, Nähen oder Reanimation in simulierten Situationen. Hinzu kommen die Famulaturen mit direktem Patientenkontakt unter ärztlicher Aufsicht.",
          "So wächst über die Klinik hinweg die praktische Routine, die im Praktischen Jahr und im Beruf gebraucht wird.",
        ],
      },
      {
        id: "vs-vorklinik",
        heading: "Unterschied zur Vorklinik und Abschluss",
        paragraphs: [
          "Während die Vorklinik die naturwissenschaftlichen Grundlagen legt, verzahnt die Klinik Theorie und Praxis am Krankheitsbild. In Modellstudiengängen ist diese Trennung stärker aufgehoben als im Regelstudiengang.",
          "Am Ende steht das zweite Staatsexamen, das vor dem Beginn des Praktischen Jahres bestanden sein muss.",
        ],
      },
    ],
    related: ["vorklinik", "physikum", "praktisches-jahr"],
    faqs: [
      {
        question: "Wann beginnt der klinische Abschnitt?",
        answer:
          "Nach bestandenem Physikum, dem ersten Staatsexamen am Ende der Vorklinik. Die Klinik erstreckt sich anschließend über mehrere Semester bis zum zweiten Staatsexamen.",
      },
      {
        question: "Was ist der Unterschied zur Vorklinik?",
        answer:
          "Die Vorklinik vermittelt naturwissenschaftliche Grundlagen wie Anatomie und Biochemie. Die Klinik verbindet Theorie und Praxis rund um konkrete Krankheitsbilder, Diagnostik und Therapie.",
      },
      {
        question: "Gibt es in der Klinik schon Patientenkontakt?",
        answer:
          "Ja. Über Skills-Labs und vor allem die Famulaturen arbeitest du unter ärztlicher Aufsicht direkt mit Patienten und baust praktische Routine für das Praktische Jahr auf.",
      },
    ],
  },
  {
    slug: "koordinierungsregeln",
    term: "Koordinierungsregeln",
    category: "Bewerbung",
    short: "Die Regeln, nach denen Angebote in der Koordinierungsphase verteilt werden.",
    definition:
      "Die Koordinierungsregeln legen fest, wie im DoSV Zulassungsangebote verteilt, angenommen oder durch höher priorisierte ersetzt werden. Du hast immer nur ein aktives Angebot: die höchste Priorität, die du gerade erreichst. Wer die Regeln kennt, vermeidet teure Fehlentscheidungen im entscheidenden Moment.",
    seoTitle: "Koordinierungsregeln im DoSV verstehen | ncrechner",
    metaDescription:
      "Koordinierungsregeln im DoSV: Warum du nur ein aktives Angebot hast, wie ein höher priorisiertes es ersetzt, warum Annehmen endgültig ist und wie du klug.",
    heroImage: "/images/blog/b-portal-stolperfallen.jpg",
    heroImageAlt: "Regeln und Stolperfallen im Bewerbungsportal der Koordinierungsphase",
    body: [],
    sections: [
      {
        id: "verteilung",
        heading: "Wie Angebote verteilt werden",
        paragraphs: [
          "Das System gleicht die Ranglisten der Hochschulen laufend mit deiner Prioritätenliste ab und erzeugt daraus genau ein aktives Angebot: die höchste Priorität, für die du gerade qualifiziert bist.",
          "Niedriger priorisierte Positionen bleiben im Hintergrund relevant, falls sich deine Lage verbessert.",
        ],
      },
      {
        id: "ersetzen",
        heading: "Priorität und Ersetzen",
        paragraphs: [
          "Erreichst du eine höher priorisierte Position, ersetzt dieses bessere Angebot automatisch das vorherige – ohne dass du etwas tun musst.",
          "Solange du ein Angebot nur stehen lässt und nicht aktiv annimmst, bleibst du für alle höher priorisierten Wünsche im Rennen.",
        ],
      },
      {
        id: "annehmen",
        heading: "Was das Annehmen bedeutet",
        paragraphs: [
          "Nimmst du ein Angebot an, verfallen alle niedriger priorisierten Wünsche sofort und unwiderruflich. Diese Entscheidung lässt sich nicht zurücknehmen.",
          "Ein zu früh angenommenes, niedriger platziertes Angebot kann dich einen besseren Platz kosten, der später noch erreichbar gewesen wäre.",
        ],
      },
      {
        id: "strategie",
        heading: "Klug priorisieren",
        paragraphs: [
          "Deine Reihenfolge steuert, welches Angebot du bekommst – nicht, ob du an einer bestimmten Uni überhaupt zugelassen wirst. Setze deshalb deinen echten Wunschort auf Platz eins, auch wenn er schwer erreichbar wirkt.",
          "Ordne nach echter Präferenz, nicht nach vermeintlicher Wahrscheinlichkeit, und nimm kein Angebot vorschnell an, solange ein höher priorisierter Wunsch möglich ist.",
        ],
      },
    ],
    related: ["koordinierungsphase", "priorisierung", "hochschulstart"],
    article: {
      label: "Koordinierungsphase verstehen",
      href: "/ratgeber/bewerbung/koordinierungsphase-verstehen/",
    },
    faqs: [
      {
        question: "Kann ich mehrere Angebote gleichzeitig haben?",
        answer:
          "Nein. Du hast immer nur ein aktives Angebot: die höchste Priorität, für die du gerade qualifiziert bist. Ein höher priorisiertes Angebot ersetzt es automatisch, sobald es erreichbar wird.",
      },
      {
        question: "Beeinflusst meine Reihenfolge die Zulassungschance?",
        answer:
          "Nein. Die Priorisierung steuert nur, welches Angebot du bekommst, nicht ob eine Uni dich zulässt. Ordne deshalb nach echter Präferenz statt nach vermuteter Wahrscheinlichkeit.",
      },
      {
        question: "Soll ich meinen schwer erreichbaren Wunsch auf Platz eins setzen?",
        answer:
          "Ja. Da die Reihenfolge nur das Wohin steuert, kostet dich eine ehrliche Priorisierung nichts. Setzt du den Wunschort nach hinten, verschenkst du im Zweifel die Chance darauf.",
      },
    ],
  },
  {
    slug: "latinum",
    term: "Latinum",
    category: "Studium",
    short: "Lateinkenntnisse – fürs Medizinstudium meist keine Voraussetzung.",
    definition:
      "Das Latinum ist der Nachweis von Lateinkenntnissen. Anders als sich hartnäckig hält, ist es für das Medizinstudium in Deutschland an den allermeisten Fakultäten keine Voraussetzung – weder für die Zulassung noch für den Abschluss. Statt eines Latinums gibt es einen Kurs in medizinischer Terminologie.",
    seoTitle: "Latinum fürs Medizinstudium: nötig oder nicht? | ncrechner",
    metaDescription:
      "Latinum fürs Medizinstudium: Warum es meist keine Voraussetzung ist, welche Rolle der Terminologiekurs spielt, wo Latein hilft und welche Mythen sich.",
    heroImage: "/images/blog/theme-pencil-notes.jpg",
    heroImageAlt: "Lateinische Fachbegriffe und Notizen im Medizinstudium",
    body: [],
    sections: [
      {
        id: "voraussetzung",
        heading: "Ist ein Latinum Pflicht?",
        paragraphs: [
          "Für das Medizinstudium in Deutschland ist ein Latinum in aller Regel keine Zulassungs- oder Abschlussvoraussetzung. Die frühere Lateinpflicht wurde längst abgeschafft.",
          "Du brauchst also kein Latein-Zertifikat, um dich zu bewerben oder das Studium abzuschließen.",
        ],
      },
      {
        id: "terminologie",
        heading: "Der Terminologiekurs",
        paragraphs: [
          "Statt eines vollständigen Latinums absolvieren Studierende meist einen Kurs in medizinischer Terminologie. Er vermittelt Grundlagen der Fachsprache: lateinische und griechische Wortstämme, anatomische Begriffe und den Aufbau medizinischer Ausdrücke.",
          "Der Kurs schließt mit einer Prüfung ab, ist aber deutlich weniger anspruchsvoll als ein klassisches Latinum.",
        ],
      },
      {
        id: "nutzen",
        heading: "Wo Latein trotzdem hilft",
        paragraphs: [
          "Ein Grundverständnis von Wortstämmen erleichtert das Lernen: Begriffe wie „Arthritis“ lassen sich in Bausteine zerlegen (arthro- für Gelenk, -itis für Entzündung). Das spart in Anatomie, Pharmakologie und Pathologie Zeit.",
          "Nötig ist ein förmliches Latinum dafür aber nicht – die relevanten Grundlagen liefert der Terminologiekurs.",
        ],
      },
      {
        id: "mythen",
        heading: "Mythen und Realität",
        paragraphs: [
          "Der Mythos „ohne Latein kein Medizinstudium“ hält sich hartnäckig, stimmt aber nicht. Auch die Idee, man müsse für die Fachbegriffe komplett Latein lernen, ist überzogen.",
          "Wer unsicher ist, prüft die Ordnung der Wunsch-Fakultät; Ausnahmen sind selten und meist als bloße Empfehlung formuliert.",
        ],
      },
    ],
    related: ["hochschulzugangsberechtigung", "vorklinik", "physikum"],
    faqs: [
      {
        question: "Brauche ich Latein fürs Medizinstudium?",
        answer:
          "In aller Regel nein. Ein Latinum ist an den meisten medizinischen Fakultäten keine Voraussetzung. Hilfreich ist lediglich ein Grundverständnis der medizinischen Fachsprache, das im Studium vermittelt wird.",
      },
      {
        question: "Was ist der Terminologiekurs?",
        answer:
          "Ein meist verpflichtender Kurs, der die Grundlagen der medizinischen Fachsprache vermittelt: lateinische und griechische Wortstämme sowie anatomische Begriffe. Er ersetzt ein volles Latinum und ist weniger anspruchsvoll.",
      },
      {
        question: "Bringt mir Latein einen Vorteil?",
        answer:
          "Ein Grundverständnis von Wortstämmen erleichtert das Lernen von Fachbegriffen in Anatomie oder Pharmakologie. Einen echten Zulassungs- oder Karrierevorteil verschafft ein förmliches Latinum aber nicht.",
      },
    ],
  },
  {
    slug: "mcat",
    term: "Medical College Admission Test (MCAT)",
    category: "Tests",
    short: "Standardisierter Zulassungstest für Medizin in den USA und Kanada.",
    definition:
      "Der MCAT ist der standardisierte Aufnahmetest für das Medizinstudium in den USA und Kanada. Er prüft naturwissenschaftliches Wissen, Textverständnis und analytisches Denken und ist wesentlich umfangreicher als der deutsche TMS. Für ein Studium in Deutschland spielt er keine Rolle.",
    seoTitle: "MCAT: Zulassungstest USA/Kanada erklärt | ncrechner",
    metaDescription:
      "MCAT erklärt: der Aufnahmetest fürs Medizinstudium in den USA und Kanada, was er prüft, wie er sich vom TMS unterscheidet und warum er für Deutschland ohne.",
    heroImage: "/images/blog/theme-laptop-online.jpg",
    heroImageAlt: "Vorbereitung auf den MCAT für das Medizinstudium in Nordamerika",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was der MCAT ist",
        paragraphs: [
          "Der MCAT ist der zentrale Aufnahmetest für das Medizinstudium in den USA und Kanada. Anders als in Deutschland beginnt das Medizinstudium dort meist erst nach einem vorherigen Bachelor-Abschluss.",
          "Der Test ist ein wichtiger Baustein der Bewerbung an nordamerikanischen medizinischen Fakultäten.",
        ],
      },
      {
        id: "inhalt",
        heading: "Was er prüft",
        paragraphs: [
          "Der MCAT deckt ein breites Spektrum ab: naturwissenschaftliche Grundlagen aus Biologie, Chemie, Physik und Biochemie, kritisches Textverständnis sowie Grundlagen aus Psychologie und Soziologie.",
          "Er ist deutlich länger und umfangreicher als der deutsche TMS und verlangt eine intensive, oft monatelange Vorbereitung.",
        ],
      },
      {
        id: "deutschland",
        heading: "Bedeutung für Deutschland",
        paragraphs: [
          "Für das Studium in Deutschland spielt der MCAT keine Rolle. Hier entscheiden Abiturnote, der TMS und die drei Quoten des zentralen Verfahrens.",
          "Relevant wird er nur, wenn du ein Studium in Nordamerika anstrebst.",
        ],
      },
      {
        id: "vs-tms",
        heading: "MCAT oder TMS?",
        paragraphs: [
          "Der TMS verbessert die Chancen im deutschen Verfahren, der MCAT ist Voraussetzung für Nordamerika. Beide prüfen zwar teils ähnliche Fähigkeiten, verfolgen aber ganz verschiedene Zwecke und Systeme.",
          "Wer in Deutschland studieren will, investiert seine Vorbereitung sinnvoll in den TMS, nicht in den MCAT.",
        ],
      },
    ],
    related: ["studierfaehigkeitstest", "tms", "ems-eignungstest"],
    faqs: [
      {
        question: "Brauche ich den MCAT für Deutschland?",
        answer:
          "Nein. Für das Medizinstudium in Deutschland ist der MCAT ohne Bedeutung. Hier zählen Abiturnote, TMS und die Kriterien der drei Quoten des zentralen Vergabeverfahrens.",
      },
      {
        question: "Worin unterscheidet sich der MCAT vom TMS?",
        answer:
          "Der MCAT ist der Zulassungstest für die USA und Kanada, deutlich umfangreicher und meist nach einem Bachelor. Der TMS ist ein freiwilliger Test, der die Chancen im deutschen Verfahren stärkt.",
      },
      {
        question: "Wann ist der MCAT für mich relevant?",
        answer:
          "Nur wenn du ein Medizinstudium in den USA oder Kanada anstrebst. Dort ist er in der Regel Teil der Bewerbung, oft zusammen mit einem vorherigen Studienabschluss.",
      },
    ],
  },
  {
    slug: "nachteilsausgleich",
    term: "Nachteilsausgleich",
    category: "Bewerbung",
    short: "Ausgleich für Nachteile durch Krankheit, Behinderung oder besondere Umstände.",
    definition:
      "Ein Nachteilsausgleich gleicht Benachteiligungen aus, die durch Krankheit, Behinderung oder besondere Lebensumstände entstehen – ohne die inhaltlichen Anforderungen zu senken. In der Zulassung kann er etwa eine verbesserte Vergleichsnote bewirken, im Studium angepasste Prüfungsbedingungen.",
    seoTitle: "Nachteilsausgleich in Zulassung & Studium | ncrechner",
    metaDescription:
      "Nachteilsausgleich erklärt: wer ihn beantragen kann, wie er die Vergleichsnote oder Prüfungsbedingungen anpasst, welche Nachweise nötig sind und wie er sich.",
    heroImage: "/images/blog/theme-helping-hands.jpg",
    heroImageAlt: "Faire Bedingungen durch einen Nachteilsausgleich",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was der Nachteilsausgleich ist",
        paragraphs: [
          "Der Nachteilsausgleich schafft faire Bedingungen für Menschen, die durch Krankheit, Behinderung oder besondere Umstände benachteiligt sind. Er passt die Rahmenbedingungen an, nicht die Inhalte oder das Anforderungsniveau.",
          "In der Bewerbung zielt er auf eine faire Bewertung der Note, im Studium auf angepasste Prüfungsbedingungen.",
        ],
      },
      {
        id: "wer",
        heading: "Wer ihn beantragen kann",
        paragraphs: [
          "In Frage kommen Bewerber und Studierende mit länger andauernder Erkrankung oder Behinderung, mit diagnostizierter Lese-Rechtschreib-Störung, in Schwangerschaft oder mit Pflege- und Betreuungsverantwortung.",
          "Für die Zulassung zählt vor allem, ob die Abiturphase nachweislich beeinträchtigt war.",
        ],
      },
      {
        id: "wirkung",
        heading: "Wie er wirkt",
        paragraphs: [
          "In der Zulassung kann auf Antrag eine verbesserte Vergleichsnote anerkannt werden, wenn belegbar ist, dass die Note ohne die Umstände besser ausgefallen wäre. Im Studium reichen die Maßnahmen von verlängerter Prüfungszeit bis zu alternativen Prüfungsformen.",
          "Der Nachteilsausgleich wird nicht im Zeugnis vermerkt.",
        ],
      },
      {
        id: "vs-haertefall",
        heading: "Abgrenzung zur Härtefallquote und Nachweise",
        paragraphs: [
          "Anders als die Härtefallquote ist der Nachteilsausgleich keine eigene Quote. Er korrigiert die Bewertung deiner Leistung, statt einen Sonderplatz zu vergeben – beide können sich aber ergänzen.",
          "Nötig sind belegbare Nachweise wie fachärztliche Atteste oder amtliche Bescheinigungen, fristgerecht eingereicht. Prüfe die genauen Regeln deiner Hochschule frühzeitig.",
        ],
      },
    ],
    related: ["haertefallquote", "abiturbestenquote", "numerus-clausus"],
    faqs: [
      {
        question: "Was bringt ein Nachteilsausgleich in der Zulassung?",
        answer:
          "Auf Antrag kann eine verbesserte Vergleichsnote anerkannt werden, wenn belegbar ist, dass deine Abiturnote durch Krankheit oder besondere Umstände beeinträchtigt wurde. Er verbessert damit deine Ausgangsposition in den notenbasierten Quoten.",
      },
      {
        question: "Wie unterscheidet er sich von der Härtefallquote?",
        answer:
          "Die Härtefallquote vergibt Sonderplätze für außergewöhnliche Notlagen. Der Nachteilsausgleich ist keine Quote, sondern korrigiert die Bewertung deiner Leistung oder die Prüfungsbedingungen. Beide können sich ergänzen.",
      },
      {
        question: "Welche Nachweise brauche ich?",
        answer:
          "Belegbare Unterlagen wie fachärztliche Atteste, Behinderungsnachweise oder amtliche Bescheinigungen über die besonderen Umstände, fristgerecht eingereicht. Die genauen Anforderungen legen Hochschule und Stelle fest.",
      },
    ],
  },
  {
    slug: "priorisierung",
    term: "Priorisierung",
    category: "Bewerbung",
    short: "Das Ordnen deiner Studienwünsche nach Wichtigkeit im Bewerbungsportal.",
    definition:
      "Priorisierung bedeutet, deine Studienwünsche im DoSV in eine persönliche Rangfolge zu bringen. Die Hochschulen sehen diese Reihenfolge nicht, sie beeinflusst deine Zulassungschance an der einzelnen Uni also nicht. Sie entscheidet aber, welches Angebot du bekommst, wenn mehrere gleichzeitig möglich sind.",
    seoTitle: "Priorisierung im DoSV richtig nutzen | ncrechner",
    metaDescription:
      "Priorisierung erklärt: Warum die Reihenfolge deiner Studienwünsche die Zulassungschance nicht beeinflusst, wozu sie dient, wie du klug ordnest und welche.",
    heroImage: "/images/blog/b-ortspraeferenzen.jpg",
    heroImageAlt: "Priorisierte Studienwünsche im Bewerbungsportal ordnen",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was Priorisierung bedeutet",
        paragraphs: [
          "Im DoSV bringst du deine Studienwünsche, also Kombinationen aus Fach und Hochschule, in eine Rangfolge. Jede Uni erstellt ihre Rangliste allein nach ihren eigenen Kriterien.",
          "Deine persönliche Reihenfolge ist für die Hochschulen dabei nicht sichtbar.",
        ],
      },
      {
        id: "keine-chance",
        heading: "Kein Einfluss auf die Zulassungschance",
        paragraphs: [
          "Ob dich eine Uni zulässt, hängt nur von deinen Werten und ihren Kriterien ab, nicht davon, an welcher Stelle du sie priorisiert hast. Die Reihenfolge steuert also das Wohin, nicht das Ob.",
          "Wichtig wird sie erst, wenn du mehrere Angebote gleichzeitig erreichen könntest.",
        ],
      },
      {
        id: "mehrere-angebote",
        heading: "Wenn mehrere Angebote möglich sind",
        paragraphs: [
          "Kämen mehrere Angebote in Frage, vergibt das System automatisch dein höchstpriorisiertes und lässt die übrigen verfallen. Du hast damit immer nur ein aktives Angebot.",
          "Deshalb solltest du deinen echten Wunschort nach vorne setzen, auch wenn er schwer erreichbar wirkt.",
        ],
      },
      {
        id: "strategie",
        heading: "Strategie und Fristen",
        paragraphs: [
          "Ordne ehrlich nach Präferenz und mische Wunsch- mit realistischen Standorten. Weil die Reihenfolge die Chance nicht senkt, kostet dich ein ehrgeiziger erster Platz nichts.",
          "Die Priorisierung lässt sich bis zum Start der Koordinierungsphase anpassen. Danach ist sie fix, kenne also die Fristen. Die ncrechner App hilft dir, realistische von ehrgeizigen Wünschen zu trennen.",
        ],
      },
    ],
    related: ["ortspraeferenz", "koordinierungsregeln", "hochschulstart"],
    article: {
      label: "Ortspräferenzen-Strategie",
      href: "/ratgeber/bewerbung/ortspraeferenzen-strategie/",
    },
    faqs: [
      {
        question: "Beeinflusst die Priorisierung meine Zulassungschance?",
        answer:
          "Nein. Die Hochschulen sehen deine Reihenfolge nicht und wählen allein nach ihren Kriterien aus. Die Priorisierung entscheidet nur, welches Angebot du bekommst, wenn mehrere gleichzeitig möglich sind.",
      },
      {
        question: "Soll ich meinen Wunschort auf Platz eins setzen?",
        answer:
          "Ja. Da die Reihenfolge die Chance nicht senkt, gehört dein echter Favorit nach vorne, auch wenn er schwer erreichbar ist. Sonst verschenkst du im Zweifel die Chance darauf.",
      },
      {
        question: "Kann ich die Priorisierung noch ändern?",
        answer:
          "Bis zum Start der Koordinierungsphase ja. Danach ist die Reihenfolge festgelegt und lässt sich nicht mehr anpassen, deshalb solltest du die Fristen genau kennen.",
      },
    ],
  },
  {
    slug: "quereinstieg",
    term: "Quereinstieg",
    category: "Grundbegriffe",
    short: "Wege ins Medizinstudium, oft als Einstieg in ein höheres Semester.",
    definition:
      "Quereinstieg beschreibt Wege ins Medizinstudium abseits des klassischen Erstbewerber-Pfads – häufig als Einstieg in ein höheres Fachsemester, bei dem bereits erbrachte Leistungen angerechnet werden. Der Weg ist kein Standard, unter bestimmten Bedingungen aber möglich.",
    seoTitle: "Quereinstieg ins Medizinstudium: Wege & Chancen | ncrechner",
    metaDescription:
      "Quereinstieg erklärt: Einstieg in höhere Semester durch Anrechnung, Wechsel aus verwandten Fächern oder aus dem Ausland, Anerkennung von Leistungen.",
    heroImage: "/images/blog/theme-thinking-choice.jpg",
    heroImageAlt: "Verschiedene Wege eines Quereinstiegs ins Medizinstudium",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was Quereinstieg bedeutet",
        paragraphs: [
          "Beim Quereinstieg wechselst du meist nicht ins erste, sondern in ein höheres Fachsemester Medizin. Voraussetzung ist, dass bereits erbrachte Studienleistungen auf den Medizin-Studiengang angerechnet werden können.",
          "Damit ist es ein Weg vor allem für Menschen mit einschlägiger akademischer Vorgeschichte.",
        ],
      },
      {
        id: "wege",
        heading: "Mögliche Wege",
        paragraphs: [
          "Am häufigsten ist die Anrechnung aus verwandten Fächern wie Zahnmedizin, Biologie, Pharmazie oder Chemie. Auch ein Studienbeginn im Ausland mit späterem Wechsel oder der Zugang über die Zweitstudienquote sind Optionen.",
          "Für die Anrechnung müssen die Inhalte den deutschen Anforderungen entsprechen, oft in Anatomie, Physiologie und Biochemie.",
        ],
      },
      {
        id: "anerkennung",
        heading: "Anerkennung von Leistungen",
        paragraphs: [
          "Über die Anrechnung entscheidet das zuständige Landesprüfungsamt. Du reichst dafür detaillierte Nachweise deiner bisherigen Kurse und Prüfungen ein; teils sind ergänzende Prüfungen nötig.",
          "Die Bewerbung läuft meist direkt bei den Hochschulen, nicht über hochschulstart.de, und oft nur zum Wintersemester.",
        ],
      },
      {
        id: "chancen",
        heading: "Wie realistisch der Quereinstieg ist",
        paragraphs: [
          "Die Plätze in höheren Semestern sind knapp, es gibt deutlich mehr Interessenten als freie Plätze. Realistischer wird der Einstieg oft erst zum klinischen Abschnitt, wenn Grundlagen bereits nachgewiesen sind.",
          "Der passende Weg hängt stark von deinem Werdegang ab, eine frühe und genaue Klärung lohnt sich.",
        ],
      },
    ],
    related: ["hochschulzugangsberechtigung", "berufsausbildung"],
    faqs: [
      {
        question: "Kann ich aus einem anderen Fach in Medizin quereinsteigen?",
        answer:
          "Unter Bedingungen ja, vor allem aus verwandten Fächern wie Zahnmedizin, Biologie oder Pharmazie. Voraussetzung ist, dass sich bereits erbrachte Leistungen auf ein höheres Medizin-Fachsemester anrechnen lassen.",
      },
      {
        question: "Wer entscheidet über die Anrechnung?",
        answer:
          "In der Regel das zuständige Landesprüfungsamt. Es prüft anhand deiner Nachweise, ob die bisherigen Kurse den deutschen Anforderungen entsprechen. Teils sind ergänzende Prüfungen nötig.",
      },
      {
        question: "Wie stehen die Chancen beim Quereinstieg?",
        answer:
          "Eher gering, weil Plätze in höheren Semestern knapp sind. Realistischer wird der Einstieg oft erst zum klinischen Abschnitt. Eine frühe, standortgenaue Klärung ist deshalb wichtig.",
      },
    ],
  },
  {
    slug: "situational-judgement-test",
    term: "Situational Judgement Test (SJT)",
    category: "Tests",
    short: "Test für soziale und kommunikative Kompetenzen im Auswahlverfahren.",
    definition:
      "Ein Situational Judgement Test (SJT) prüft, wie Bewerber in typischen berufsnahen Situationen reagieren würden. Er misst soziale und kommunikative Kompetenzen statt Fachwissen und wird an einzelnen Hochschulen ergänzend im Auswahlverfahren oder als Teil eines Tests wie dem HAM-Nat eingesetzt.",
    seoTitle: "Situational Judgement Test (SJT) erklärt | ncrechner",
    metaDescription:
      "SJT erklärt: Was der Situational Judgement Test prüft, wie die Szenario-Aufgaben funktionieren, wo er eingesetzt wird und wie er kognitive Tests wie den TMS.",
    heroImage: "/images/blog/z-sjt.jpg",
    heroImageAlt: "Szenario-Aufgaben eines Situational Judgement Tests",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was der SJT ist",
        paragraphs: [
          "Der SJT misst nicht Wissen, sondern Urteilsvermögen in berufsnahen Situationen. Er soll einschätzen, wie gut jemand mit sozialen und kommunikativen Anforderungen des Arztberufs umgehen könnte.",
          "Damit ergänzt er kognitive Tests um eine zwischenmenschliche Dimension.",
        ],
      },
      {
        id: "aufgaben",
        heading: "Wie die Aufgaben funktionieren",
        paragraphs: [
          "Vorgegeben wird eine typische Situation, dazu mehrere mögliche Reaktionen. Du bewertest, wie angemessen oder unangemessen diese sind. Es gibt selten ein klares Richtig oder Falsch, sondern besser und schlechter passende Antworten.",
          "Bewertet wird, wie nah deine Einschätzung an einem Expertenkonsens liegt.",
        ],
      },
      {
        id: "einsatz",
        heading: "Wo der SJT eingesetzt wird",
        paragraphs: [
          "Der SJT ist nur an bestimmten Standorten relevant, teils als eigener Baustein, teils integriert in einen naturwissenschaftlichen Test. Seine Bedeutung nimmt langsam zu, ist aber kein bundesweiter Standard.",
          "Prüfe deshalb, ob deine Wunsch-Hochschule ihn überhaupt einsetzt.",
        ],
      },
      {
        id: "vorbereitung",
        heading: "Vorbereitung",
        paragraphs: [
          "Anders als beim reinen Wissenstest lässt sich der SJT weniger stur lernen. Übung mit Beispielszenarien hilft aber, das erwartete Antwortverhalten und typische Fallstricke kennenzulernen.",
          "Kombiniert mit einem guten TMS rundet er ein soziales Profil sinnvoll ab.",
        ],
      },
    ],
    related: ["studierfaehigkeitstest", "tms", "auswahlverfahren-der-hochschulen"],
    article: {
      label: "Situational Judgement Test (SJT)",
      href: "/ratgeber/zulassung/situational-judgement-test-sjt-medizin/",
    },
    faqs: [
      {
        question: "Was prüft der SJT?",
        answer:
          "Soziale und kommunikative Kompetenzen: wie angemessen du in berufsnahen Situationen reagieren würdest. Fachwissen wird nicht abgefragt, es geht um Urteilsvermögen in geschilderten Szenarien.",
      },
      {
        question: "Kann man den SJT vorbereiten?",
        answer:
          "Nur begrenzt lernen, aber üben. Beispielszenarien helfen, das erwartete Antwortverhalten und typische Fallstricke kennenzulernen. Ein reines Auswendiglernen wie bei einem Wissenstest ist nicht möglich.",
      },
      {
        question: "Wo wird der SJT eingesetzt?",
        answer:
          "Nur an einzelnen Hochschulen, teils als eigener Baustein, teils integriert in einen Test wie den HAM-Nat. Prüfe vor der Vorbereitung, ob dein Wunschort ihn überhaupt verwendet.",
      },
    ],
  },
  {
    slug: "studienplatzklage",
    term: "Studienplatzklage",
    category: "Grundbegriffe",
    apoBankCta: "studierendenpaket",
    short: "Der juristische Versuch, einen Studienplatz über nicht genutzte Kapazität zu erhalten.",
    definition:
      "Bei einer Studienplatzklage versuchen Bewerber, über den Rechtsweg einen zusätzlichen Studienplatz zu erstreiten – mit dem Argument, eine Hochschule habe ihre Ausbildungskapazität nicht voll ausgeschöpft. Der Weg ist aufwendig, teuer und im Ausgang unsicher.",
    seoTitle: "Studienplatzklage Medizin: Chancen & Kosten | ncrechner",
    metaDescription:
      "Studienplatzklage erklärt: Wie die Kapazitätsklage funktioniert, welche Kosten (oft mehrere Tausend Euro) entstehen, welche Fristen gelten.",
    heroImage: "/images/blog/z-studienplatzklage.jpg",
    heroImageAlt: "Rechtsweg der Studienplatzklage um einen Medizin-Studienplatz",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was eine Studienplatzklage ist",
        paragraphs: [
          "Die Studienplatzklage stützt sich auf den Gedanken, dass Hochschulen ihre tatsächliche Kapazität voll ausschöpfen müssen. Bleiben rechnerisch Plätze ungenutzt, lassen sich diese unter Umständen gerichtlich einfordern.",
          "Sie richtet sich also nicht gegen deine Ablehnung an sich, sondern gegen die Kapazitätsberechnung der Uni.",
        ],
      },
      {
        id: "ablauf",
        heading: "Wie sie abläuft",
        paragraphs: [
          "In der Regel beauftragst du einen auf Hochschulrecht spezialisierten Anwalt. Das Gericht prüft die Kapazitätsberechnung; am Ende werden entweder zusätzliche Plätze festgestellt und verteilt oder die Klage abgewiesen.",
          "Oft klagen viele parallel gegen dieselbe Uni, was die individuelle Chance beeinflusst.",
        ],
      },
      {
        id: "kosten",
        heading: "Kosten und Fristen",
        paragraphs: [
          "Die Kosten sind erheblich und können sich pro Hochschule auf mehrere Tausend Euro summieren, aus Gerichts- und Anwaltskosten. Wer verliert, trägt meist den Großteil.",
          "Die Fristen sind knapp und je Bundesland unterschiedlich, oft im Zeitraum kurz nach der Ablehnung. Formfehler führen schnell zum Scheitern.",
        ],
      },
      {
        id: "chancen",
        heading: "Chancen und Einordnung",
        paragraphs: [
          "Die Erfolgsaussichten schwanken stark nach Hochschule, Jahr und formaler Sorgfalt und sind insgesamt unsicher. Eine Studienplatzklage ersetzt keine Strategie über die regulären Quoten.",
          "Sinnvoll ist sie bestenfalls als ergänzende, gut abgewogene Option, nicht als erster Plan.",
        ],
      },
    ],
    related: ["ablehnungsbescheid", "losverfahren", "numerus-clausus"],
    article: {
      label: "Studienplatzklage: Chancen & Kosten",
      href: "/ratgeber/zulassung/studienplatzklage-medizin-chancen-kosten/",
    },
    faqs: [
      {
        question: "Was kostet eine Studienplatzklage?",
        answer:
          "Je Hochschule oft mehrere Tausend Euro aus Gerichts- und Anwaltskosten. Wer verliert, trägt in der Regel den Großteil. Wegen der Kosten klagen viele gezielt an mehreren Unis parallel.",
      },
      {
        question: "Wie hoch sind die Erfolgschancen?",
        answer:
          "Sehr unterschiedlich und insgesamt unsicher. Sie hängen von Hochschule, Jahr, Zahl der Kläger und formaler Sorgfalt ab. Eine Garantie gibt es nicht, und Formfehler führen schnell zum Scheitern.",
      },
      {
        question: "Ersetzt die Klage die reguläre Bewerbung?",
        answer:
          "Nein. Sie ist bestenfalls eine ergänzende Option. Bewirb dich weiterhin regulär über die Quoten und prüfe Nachrücken und Losverfahren, statt allein auf die Klage zu setzen.",
      },
    ],
  },
  {
    slug: "studienplatztausch",
    term: "Studienplatztausch",
    category: "Bewerbung",
    short: "Der Tausch von Studienplätzen zwischen Studierenden verschiedener Orte.",
    definition:
      "Beim Studienplatztausch tauschen zwei bereits immatrikulierte Studierende ihre Studienplätze an unterschiedlichen Hochschulen. So lässt sich der Studienort wechseln, ohne sich neu über das zentrale Verfahren bewerben zu müssen – gerade in NC-Fächern oft der einzige praktikable Weg.",
    seoTitle: "Studienplatztausch Medizin: Ort wechseln | ncrechner",
    metaDescription:
      "Studienplatztausch erklärt: Wie zwei Studierende ihre Plätze tauschen, welche Voraussetzungen gelten, wie du über Tauschbörsen einen Partner findest.",
    heroImage: "/images/blog/theme-laptop-online.jpg",
    heroImageAlt: "Suche nach einem Tauschpartner für den Studienplatztausch online",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was der Studienplatztausch ist",
        paragraphs: [
          "Zwei immatrikulierte Studierende tauschen ihre Plätze an verschiedenen Hochschulen. Da reguläre Wechsel in NC-Fächern wie Medizin kaum möglich sind, ist der Tausch oft der einzige praktikable Weg, den Ort zu wechseln.",
          "Beide Hochschulen müssen dem Tausch zustimmen.",
        ],
      },
      {
        id: "voraussetzungen",
        heading: "Voraussetzungen",
        paragraphs: [
          "In der Regel müssen beide im selben Fach und im selben Studienabschnitt sein, damit die Leistungen übertragbar sind. Viele Hochschulen schließen einen Tausch im ersten Semester aus.",
          "Beide müssen regulär immatrikuliert sein; die Studierendensekretariate beider Unis prüfen und genehmigen den Tausch.",
        ],
      },
      {
        id: "partner",
        heading: "Wie du einen Partner findest",
        paragraphs: [
          "Passende Partner findet man über spezialisierte Tauschbörsen und Foren, die Einzel- und teils Ringtausche vermitteln. Je mehr Menschen von deinem Tauschwunsch wissen, desto größer die Chance auf ein Match.",
          "Ein Tausch klappt nur, wenn zwei Wünsche zueinander passen, das kann dauern.",
        ],
      },
      {
        id: "fristen",
        heading: "Fristen und Nebeneffekte",
        paragraphs: [
          "Es gelten feste Stichtage je Semester, die die Hochschulen selbst festlegen. Prüfe die Termine auf beiden Seiten frühzeitig.",
          "Beachte Nebeneffekte, etwa auf BAföG oder auf den Studienverlauf, wenn die Curricula nicht exakt gleich aufgebaut sind.",
        ],
      },
    ],
    related: ["ortspraeferenz", "nachruecken", "quereinstieg"],
    faqs: [
      {
        question: "Wer kann einen Studienplatz tauschen?",
        answer:
          "Zwei immatrikulierte Studierende desselben Fachs, in der Regel im selben Studienabschnitt. Beide Hochschulen müssen zustimmen, und ein Tausch im ersten Semester ist an vielen Unis ausgeschlossen.",
      },
      {
        question: "Wie finde ich einen Tauschpartner?",
        answer:
          "Über spezialisierte Tauschbörsen und Foren, die Einzel- und Ringtausche vermitteln. Ein Match klappt nur, wenn zwei Tauschwünsche zueinander passen, das kann einige Zeit dauern.",
      },
      {
        question: "Worauf muss ich beim Tausch achten?",
        answer:
          "Auf die Fristen beider Hochschulen, die Übertragbarkeit deiner Leistungen und Nebeneffekte etwa beim BAföG. Kläre unterschiedliche Curricula frühzeitig, damit keine Studienzeit verloren geht.",
      },
    ],
  },
  {
    slug: "talentquote",
    term: "Talentquote",
    category: "Quoten",
    short: "Umgangssprachlicher Begriff, meist für die Zusätzliche Eignungsquote (ZEQ).",
    definition:
      "„Talentquote“ ist kein offizieller Begriff, sondern eine umgangssprachliche Bezeichnung. Meist ist damit die Zusätzliche Eignungsquote (ZEQ) gemeint, die rund 10 Prozent der Plätze notenunabhängig nach Eignung vergibt. Die Idee: Wer über den Abischnitt hinaus besondere Stärken zeigt, soll eine Chance bekommen.",
    seoTitle: "Talentquote: Was hinter dem Begriff steckt | ncrechner",
    metaDescription:
      "Talentquote erklärt: Warum es kein offizieller Begriff ist, dass meist die ZEQ (rund 10 % der Plätze) gemeint ist, welche Kriterien zählen und für wen sich.",
    heroImage: "/images/blog/z-wettbewerbe.jpg",
    heroImageAlt: "Besondere Leistungen und Talente als Kriterium der Talentquote",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was mit Talentquote gemeint ist",
        paragraphs: [
          "In den Regelungen gibt es keine Quote namens „Talentquote“. Der Begriff hat sich seit der Reform 2020 in Medien und Foren eingebürgert und meint fast immer die Zusätzliche Eignungsquote (ZEQ).",
          "Die ZEQ vergibt rund 10 Prozent der Plätze und lässt die Abiturnote außen vor.",
        ],
      },
      {
        id: "kriterien",
        heading: "Welche Kriterien zählen",
        paragraphs: [
          "Statt der Note zählen Eignungsmerkmale: ein Studierfähigkeitstest wie der TMS, eine abgeschlossene Ausbildung, ein Dienst, anerkannte Wettbewerbe oder ein Auswahlgespräch. Jede Hochschule gewichtet selbst.",
          "An mehreren Standorten entscheidet allein der TMS über diese Quote.",
        ],
      },
      {
        id: "abgrenzung",
        heading: "Abgrenzung zu anderen Quoten",
        paragraphs: [
          "Während die Abiturbestenquote (rund 30 Prozent) allein die Note wertet und das AdH (rund 60 Prozent) Note und weitere Kriterien mischt, ist die „Talentquote“ die eine Quote, in der die Note gar nicht zählt.",
          "Vereinzelt nutzen Bundesländer den Begriff auch für eigene Programme, die besondere Talente fördern.",
        ],
      },
      {
        id: "fuer-wen",
        heading: "Für wen sie sich lohnt",
        paragraphs: [
          "Profitieren vor allem Bewerber mit mittlerem oder schwächerem Abitur, die ein starkes Zusatzprofil vorweisen können, etwa einen sehr guten TMS oder eine einschlägige Ausbildung.",
          "Mit der ncrechner App siehst du, an welchen Hochschulen dein Profil in dieser Quote reicht.",
        ],
      },
    ],
    related: ["zusaetzliche-eignungsquote", "anerkannter-preis", "landarztquote"],
    faqs: [
      {
        question: "Gibt es die Talentquote offiziell?",
        answer:
          "Nein. „Talentquote“ ist ein umgangssprachlicher Begriff. Gemeint ist fast immer die Zusätzliche Eignungsquote (ZEQ), die rund 10 Prozent der Plätze notenunabhängig nach Eignung vergibt.",
      },
      {
        question: "Welche Kriterien zählen in der Talentquote?",
        answer:
          "Je nach Hochschule ein Studierfähigkeitstest wie der TMS, eine Ausbildung, ein Dienst, anerkannte Wettbewerbe oder ein Auswahlgespräch. Die Abiturnote spielt keine Rolle.",
      },
      {
        question: "Für wen lohnt sich dieser Weg?",
        answer:
          "Für Bewerber mit mittlerem oder schwächerem Abitur und einem starken Zusatzprofil, etwa einem sehr guten TMS oder einer abgeschlossenen medizinnahen Ausbildung.",
      },
    ],
  },
  {
    slug: "testas",
    term: "TestAS",
    category: "Tests",
    short: "Studierfähigkeitstest für internationale Studienbewerber.",
    definition:
      "Der TestAS ist ein standardisierter Studierfähigkeitstest für internationale Bewerber, die in Deutschland studieren möchten. Er prüft allgemeine und fachbezogene kognitive Fähigkeiten und hilft Hochschulen, die Eignung von Bewerbern mit ausländischem Schulabschluss einzuschätzen.",
    seoTitle: "TestAS: Studierfähigkeitstest für Internationale | ncrechner",
    metaDescription:
      "TestAS erklärt: der Studierfähigkeitstest für internationale Bewerber in Deutschland, was er prüft, wie er aufgebaut ist, in welchen Sprachen er stattfindet.",
    heroImage: "/images/blog/t-textverstaendnis.jpg",
    heroImageAlt: "Internationale Bewerber beim TestAS für ein Studium in Deutschland",
    body: [],
    sections: [
      {
        id: "was-ist",
        heading: "Was der TestAS ist",
        paragraphs: [
          "Der TestAS richtet sich an internationale Studieninteressierte, die einen ausländischen Schulabschluss haben. Er gibt Hochschulen einen standardisierten Anhaltspunkt für die Studierfähigkeit, unabhängig vom jeweiligen Schulsystem.",
          "Ein gutes Ergebnis ist für viele internationale Bewerbungen ein wichtiger Baustein.",
        ],
      },
      {
        id: "aufbau",
        heading: "Aufbau des Tests",
        paragraphs: [
          "Der TestAS besteht aus einem Kerntest, der allgemeine kognitive Fähigkeiten prüft, und einem fachspezifischen Modul, das zur angestrebten Studienrichtung passt.",
          "Geprüft werden unter anderem logisches Denken, das Erkennen von Zusammenhängen und Textverständnis.",
        ],
      },
      {
        id: "sprachen",
        heading: "Sprachen und Termine",
        paragraphs: [
          "Der Test wird in mehreren Sprachen angeboten, darunter Deutsch und Englisch, und mehrmals im Jahr durchgeführt. So lässt er sich in vielen Ländern ablegen.",
          "Das Ergebnis ist über einen längeren Zeitraum gültig und kann für mehrere Bewerbungen genutzt werden.",
        ],
      },
      {
        id: "wofuer",
        heading: "Wofür der TestAS gebraucht wird",
        paragraphs: [
          "Manche Hochschulen setzen den TestAS voraus oder werten ihn im Auswahlverfahren für internationale Bewerber. Für deutsche Abiturienten ist er dagegen nicht relevant, hier zählt der TMS.",
          "Kläre früh, ob deine Ziel-Hochschule den TestAS verlangt und welches Fachmodul passt.",
        ],
      },
    ],
    related: ["studierfaehigkeitstest", "hochschulzugangsberechtigung", "tms"],
    faqs: [
      {
        question: "Für wen ist der TestAS gedacht?",
        answer:
          "Für internationale Bewerber mit ausländischem Schulabschluss, die in Deutschland studieren möchten. Für deutsche Abiturienten ist er nicht relevant, dort zählt der TMS.",
      },
      {
        question: "Wie ist der TestAS aufgebaut?",
        answer:
          "Aus einem Kerntest zu allgemeinen kognitiven Fähigkeiten und einem fachspezifischen Modul passend zur Studienrichtung. Geprüft werden unter anderem logisches Denken und Textverständnis.",
      },
      {
        question: "In welchen Sprachen kann ich den TestAS ablegen?",
        answer:
          "In mehreren Sprachen, darunter Deutsch und Englisch, und mehrmals im Jahr. Das Ergebnis ist über einen längeren Zeitraum gültig und für mehrere Bewerbungen nutzbar.",
      },
    ],
  },
];

/** Alphabetisch sortierte Begriffsliste (deutsche Sortierung). */
export const lexikonSorted: LexikonTerm[] = [...lexikonTerms].sort((a, b) =>
  collator.compare(a.term, b.term),
);

/** Vollständiges Alphabet für die A–Z-Navigation. */
export const ALPHABET = "ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("");

/** Kategorien in sinnvoller Anzeigereihenfolge (für den Kategorie-Filter). */
export const LEXIKON_CATEGORIES: LexikonTerm["category"][] = [
  "Grundbegriffe",
  "Quoten",
  "Tests",
  "Bewerbung",
  "Studium",
];

/** Buchstaben, zu denen es mindestens einen Begriff gibt. */
export function lexikonActiveLetters(): Set<string> {
  return new Set(lexikonTerms.map((t) => firstLetter(t.term)));
}

export function getTerm(slug: string): LexikonTerm | undefined {
  return lexikonTerms.find((t) => t.slug === slug);
}

export function lexikonPath(slug: string): string {
  return `/lexikon/${slug}/`;
}

/** Kategorie-Standardbild, falls ein Begriff kein eigenes `heroImage` hat. */
const CATEGORY_HERO: Record<LexikonTerm["category"], string> = {
  Grundbegriffe: "/images/blog/medizin-zulassung-guide.jpg",
  Quoten: "/images/blog/studienplatzvergabe-medizin.jpg",
  Tests: "/images/blog/t-untertests-ueberblick.jpg",
  Bewerbung: "/images/blog/b-hochschulstart-anleitung.jpg",
  Studium: "/images/blog/theme-hospital-corridor.jpg",
};

/** Liefert für jeden Begriff ein Titelbild – eigenes oder Kategorie-Standard. */
export function heroFor(term: LexikonTerm): { src: string; alt: string } {
  if (term.heroImage) return { src: term.heroImage, alt: term.heroImageAlt ?? "" };
  return { src: CATEGORY_HERO[term.category], alt: `${term.term} – ncrechner Medizin-Lexikon` };
}

/**
 * "Für wen"-Satz je Kategorie (AEO: Zielgruppen-/Anwendungsfall-Klarheit in
 * der AnswerBox). Ein Satz pro Kategorie, wiederverwendet über alle Begriffe
 * dieser Kategorie – für jeden Begriff darin wahr, nicht pro Begriff erfunden.
 */
const AUDIENCE_BY_CATEGORY: Record<LexikonTerm["category"], string> = {
  Quoten:
    "Für alle mit laufender oder geplanter Bewerbung im Vergabeverfahren, die verstehen wollen, wie diese Quote ihre Zulassungschance beeinflusst.",
  Tests:
    "Für Bewerber, die prüfen wollen, ob dieser Test an ihren Wunsch-Hochschulen zählt und wie sie sich darauf vorbereiten.",
  Bewerbung:
    "Für alle, die sich aktuell über hochschulstart.de bewerben oder ihre Bewerbung vorbereiten.",
  Studium:
    "Für angehende und zukünftige Studierende, die sich einen Überblick über das Studium verschaffen wollen.",
  Grundbegriffe:
    "Für alle, die sich neu mit der Zulassung zum Medizinstudium beschäftigen und zentrale Begriffe einordnen wollen.",
};

/** Kategorie-basierter "Für wen"-Satz für die AnswerBox eines Lexikon-Eintrags. */
export function audienceForCategory(category: LexikonTerm["category"]): string {
  return AUDIENCE_BY_CATEGORY[category];
}

/**
 * Verwandte Begriffe auflösen: zuerst die explizit gesetzten `related`-Slugs,
 * danach – falls zu wenige – Begriffe derselben Kategorie als Fallback.
 */
export function relatedTermsFor(term: LexikonTerm, max = 4): LexikonTerm[] {
  const picked: LexikonTerm[] = [];
  const seen = new Set<string>([term.slug]);

  for (const slug of term.related ?? []) {
    const found = getTerm(slug);
    if (found && !seen.has(found.slug)) {
      picked.push(found);
      seen.add(found.slug);
    }
  }

  if (picked.length < max) {
    for (const candidate of lexikonSorted) {
      if (picked.length >= max) break;
      if (seen.has(candidate.slug)) continue;
      if (candidate.category === term.category) {
        picked.push(candidate);
        seen.add(candidate.slug);
      }
    }
  }

  return picked.slice(0, max);
}
